import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, TextField, Button, Typography, CircularProgress, Avatar, Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, IconButton, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Chip, Switch, FormControlLabel, Popover, Paper, ClickAwayListener } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
// (SkipNextIcon import removed — skip uses ⏭ emoji now)
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MicIcon from '@mui/icons-material/Mic';
import DOMPurify from 'dompurify';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CallIcon from '@mui/icons-material/Call';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import ArticleIcon from '@mui/icons-material/Article';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { connectSocket, joinConversation, leaveConversation } from '../../api/socket';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const AUTH_API = process.env.REACT_APP_AUTH_API || 'http://localhost:5000/api/auth';
const BOTS_API = AUTH_API.replace('/api/auth', '/api/bots');
const CONV_API = AUTH_API.replace('/api/auth', '/api/conversation');
const UPLOAD_API = AUTH_API.replace('/api/auth', '/api/upload');

const getOrCreateSid = (chatId) => {
  const key = `talktokam_sid_${chatId}`;
  let sid = localStorage.getItem(key);
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(key, sid);
  }
  return sid;
};
const saveState = (chatId, state) => {
  try { localStorage.setItem(`talktokam_state_${chatId}`, JSON.stringify(state)); } catch { }
};
const loadState = (chatId) => {
  try { return JSON.parse(localStorage.getItem(`talktokam_state_${chatId}`)) || null; } catch { return null; }
};
const clearSession = (chatId) => {
  localStorage.removeItem(`talktokam_sid_${chatId}`);
  localStorage.removeItem(`talktokam_state_${chatId}`);
};

const uploadViaBackend = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(UPLOAD_API, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url;
};

const sanitizeHtml = (input) => {
  if (!input || typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'span', 'div', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'code', 'pre', 'blockquote', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style'],
    ADD_ATTR: ['target'],
  });
};

const validateAnswer = (answer, question) => {
  if (!question) return { valid: true };
  const { type, validations } = question;
  const msg = question.errorMessage || 'Please enter a valid answer';
  if (type === 'email_feild' || type === 'email') {
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(answer)) return { valid: false, error: msg };
  }
  if (type === 'website') {
    if (!/^https?:\/\/.+\..+/.test(answer.trim())) return { valid: false, error: msg || 'Please enter a valid URL (https://...)' };
  }
  if (type === 'number') {
    if (answer.trim() === '' || isNaN(Number(answer))) return { valid: false, error: msg };
    const num = Number(answer);
    if (validations?.minLength != null && num < validations.minLength) return { valid: false, error: msg };
    if (validations?.maxLength != null && num > validations.maxLength) return { valid: false, error: msg };
  }
  if (type === 'mobile_number') {
    if (answer.replace(/\D/g, '') !== answer.trim()) return { valid: false, error: msg };
    if (validations?.minLength != null && answer.length < validations.minLength) return { valid: false, error: msg };
    if (validations?.maxLength != null && answer.length > validations.maxLength) return { valid: false, error: msg };
  }
  if (validations?.minLength != null && answer.length < validations.minLength) return { valid: false, error: msg };
  if (validations?.maxLength != null && answer.length > validations.maxLength) return { valid: false, error: msg };
  if (validations?.pattern) {
    try {
      if (!new RegExp(validations.pattern).test(answer)) return { valid: false, error: msg };
    } catch { }
  }
  return { valid: true };
};

const TRANSLATIONS = {
  English: {
    closeTitle: 'Close this chat?',
    closeBody: 'Your chat history will be cleared and you\'ll start a new conversation. This cannot be undone.',
    closeCancel: 'Cancel',
    closeConfirm: 'Close Chat',
    preChatName: 'Before we start, what\'s your name?',
    namePlaceholder: 'Enter your name...',
    startChat: 'Start Chat',
    closedTitle: 'Conversation Closed',
    closedBody: 'This chat has been closed by the support team.',
    reopen: 'Reopen Chat',
    tapOption: 'Tap an option to continue',
    selectSubmit: 'Select and submit',
  },
  Hindi: {
    closeTitle: 'à¤šà¥ˆà¤Ÿ à¤¬à¤‚à¤¦ à¤•à¤°à¥‡à¤‚?',
    closeBody: 'à¤†à¤ªà¤•à¤¾ à¤šà¥ˆà¤Ÿ à¤‡à¤¤à¤¿à¤¹à¤¾à¤¸ à¤¸à¤¾à¤«à¤¼ à¤¹à¥‹ à¤œà¤¾à¤à¤—à¤¾ à¤”à¤° à¤†à¤ª à¤à¤• à¤¨à¤ˆ à¤¬à¤¾à¤¤à¤šà¥€à¤¤ à¤¶à¥à¤°à¥‚ à¤•à¤°à¥‡à¤‚à¤—à¥‡à¥¤ à¤‡à¤¸à¥‡ à¤ªà¥‚à¤°à¥à¤µà¤µà¤¤ à¤¨à¤¹à¥€à¤‚ à¤•à¤¿à¤¯à¤¾ à¤œà¤¾ à¤¸à¤•à¤¤à¤¾à¥¤',
    closeCancel: 'à¤°à¤¦à¥à¤¦ à¤•à¤°à¥‡à¤‚',
    closeConfirm: 'à¤šà¥ˆà¤Ÿ à¤¬à¤‚à¤¦ à¤•à¤°à¥‡à¤‚',
    preChatName: 'à¤¶à¥à¤°à¥‚ à¤•à¤°à¤¨à¥‡ à¤¸à¥‡ à¤ªà¤¹à¤²à¥‡, à¤†à¤ªà¤•à¤¾ à¤¨à¤¾à¤® à¤•à¥à¤¯à¤¾ à¤¹à¥ˆ?',
    namePlaceholder: 'à¤…à¤ªà¤¨à¤¾ à¤¨à¤¾à¤® à¤¦à¤°à¥à¤œ à¤•à¤°à¥‡à¤‚...',
    startChat: 'à¤šà¥ˆà¤Ÿ à¤¶à¥à¤°à¥‚ à¤•à¤°à¥‡à¤‚',
    closedTitle: 'à¤¬à¤¾à¤¤à¤šà¥€à¤¤ à¤¬à¤‚à¤¦ à¤¹à¥à¤ˆ',
    closedBody: 'à¤¯à¤¹ à¤šà¥ˆà¤Ÿ à¤¸à¤¹à¤¾à¤¯à¤¤à¤¾ à¤Ÿà¥€à¤® à¤¦à¥à¤µà¤¾à¤°à¤¾ à¤¬à¤‚à¤¦ à¤•à¤° à¤¦à¥€ à¤—à¤ˆ à¤¹à¥ˆà¥¤',
    reopen: 'à¤šà¥ˆà¤Ÿ à¤«à¤¿à¤° à¤¸à¥‡ à¤–à¥‹à¤²à¥‡à¤‚',
    tapOption: 'à¤œà¤¾à¤°à¥€ à¤°à¤–à¤¨à¥‡ à¤•à¥‡ à¤²à¤¿à¤ à¤µà¤¿à¤•à¤²à¥à¤ª à¤ªà¤° à¤Ÿà¥ˆà¤ª à¤•à¤°à¥‡à¤‚',
    selectSubmit: 'à¤šà¥à¤¨à¥‡à¤‚ à¤”à¤° à¤œà¤®à¤¾ à¤•à¤°à¥‡à¤‚',
  },
  Bengali: {
    closeTitle: 'à¦šà§à¦¯à¦¾à¦Ÿ à¦¬à¦¨à§à¦§ à¦•à¦°à¦¬à§‡à¦¨?',
    closeBody: 'à¦†à¦ªà¦¨à¦¾à¦° à¦šà§à¦¯à¦¾à¦Ÿ à¦‡à¦¤à¦¿à¦¹à¦¾à¦¸ à¦®à§à¦›à§‡ à¦¯à¦¾à¦¬à§‡ à¦à¦¬à¦‚ à¦†à¦ªà¦¨à¦¿ à¦à¦•à¦Ÿà¦¿ à¦¨à¦¤à§à¦¨ à¦•à¦¥à§‹à¦ªà¦•à¦¥à¦¨ à¦¶à§à¦°à§ à¦•à¦°à¦¬à§‡à¦¨à¥¤ à¦à¦Ÿà¦¿ à¦ªà§‚à¦°à§à¦¬à¦¾à¦¬à¦¸à§à¦¥à¦¾à¦¯à¦¼ à¦«à§‡à¦°à¦¾à¦¨à§‹ à¦¯à¦¾à¦¬à§‡ à¦¨à¦¾à¥¤',
    closeCancel: 'à¦¬à¦¾à¦¤à¦¿à¦² à¦•à¦°à§à¦¨',
    closeConfirm: 'à¦šà§à¦¯à¦¾à¦Ÿ à¦¬à¦¨à§à¦§ à¦•à¦°à§à¦¨',
    preChatName: 'à¦¶à§à¦°à§ à¦•à¦°à¦¾à¦° à¦†à¦—à§‡, à¦†à¦ªà¦¨à¦¾à¦° à¦¨à¦¾à¦® à¦•à§€?',
    namePlaceholder: 'à¦†à¦ªà¦¨à¦¾à¦° à¦¨à¦¾à¦® à¦²à¦¿à¦–à§à¦¨...',
    startChat: 'à¦šà§à¦¯à¦¾à¦Ÿ à¦¶à§à¦°à§ à¦•à¦°à§à¦¨',
    closedTitle: 'à¦•à¦¥à§‹à¦ªà¦•à¦¥à¦¨ à¦¬à¦¨à§à¦§',
    closedBody: 'à¦à¦‡ à¦šà§à¦¯à¦¾à¦Ÿà¦Ÿà¦¿ à¦¸à¦¹à¦¾à¦¯à¦¼à¦¤à¦¾ à¦¦à¦² à¦¦à§à¦¬à¦¾à¦°à¦¾ à¦¬à¦¨à§à¦§ à¦•à¦°à¦¾ à¦¹à¦¯à¦¼à§‡à¦›à§‡à¥¤',
    reopen: 'à¦šà§à¦¯à¦¾à¦Ÿ à¦ªà§à¦¨à¦°à¦¾à¦¯à¦¼ à¦–à§à¦²à§à¦¨',
    tapOption: 'à¦šà¦¾à¦²à¦¿à¦¯à¦¼à§‡ à¦¯à§‡à¦¤à§‡ à¦à¦•à¦Ÿà¦¿ à¦¬à¦¿à¦•à¦²à§à¦ªà§‡ à¦Ÿà§à¦¯à¦¾à¦ª à¦•à¦°à§à¦¨',
    selectSubmit: 'à¦¨à¦¿à¦°à§à¦¬à¦¾à¦šà¦¨ à¦•à¦°à§à¦¨ à¦à¦¬à¦‚ à¦œà¦®à¦¾ à¦¦à¦¿à¦¨',
  },
  Marathi: {
    closeTitle: 'à¤šà¥…à¤Ÿ à¤¬à¤‚à¤¦ à¤•à¤°à¤¾à¤¯à¤šà¥€?',
    closeBody: 'à¤¤à¥à¤®à¤šà¤¾ à¤šà¥…à¤Ÿ à¤‡à¤¤à¤¿à¤¹à¤¾à¤¸ à¤¸à¤¾à¤« à¤¹à¥‹à¤ˆà¤² à¤†à¤£à¤¿ à¤¤à¥à¤®à¥à¤¹à¥€ à¤¨à¤µà¥€à¤¨ à¤¸à¤‚à¤­à¤¾à¤·à¤£ à¤¸à¥à¤°à¥‚ à¤•à¤°à¤¾à¤². à¤¹à¥‡ à¤ªà¥‚à¤°à¥à¤µà¤µà¤¤ à¤•à¤°à¤¤à¤¾ à¤¯à¥‡à¤£à¤¾à¤° à¤¨à¤¾à¤¹à¥€.',
    closeCancel: 'à¤°à¤¦à¥à¤¦ à¤•à¤°à¤¾',
    closeConfirm: 'à¤šà¥…à¤Ÿ à¤¬à¤‚à¤¦ à¤•à¤°à¤¾',
    preChatName: 'à¤¸à¥à¤°à¥‚ à¤•à¤°à¤£à¥à¤¯à¤¾à¤ªà¥‚à¤°à¥à¤µà¥€, à¤¤à¥à¤®à¤šà¥‡ à¤¨à¤¾à¤µ à¤•à¤¾à¤¯ à¤†à¤¹à¥‡?',
    namePlaceholder: 'à¤¤à¥à¤®à¤šà¥‡ à¤¨à¤¾à¤µ à¤ªà¥à¤°à¤µà¤¿à¤·à¥à¤Ÿ à¤•à¤°à¤¾...',
    startChat: 'à¤šà¥…à¤Ÿ à¤¸à¥à¤°à¥‚ à¤•à¤°à¤¾',
    closedTitle: 'à¤¸à¤‚à¤­à¤¾à¤·à¤£ à¤¬à¤‚à¤¦',
    closedBody: 'à¤¹à¥‡ à¤šà¥…à¤Ÿ à¤¸à¤¹à¤¾à¤¯à¥à¤¯ à¤Ÿà¥€à¤®à¤¨à¥‡ à¤¬à¤‚à¤¦ à¤•à¥‡à¤²à¥‡ à¤†à¤¹à¥‡.',
    reopen: 'à¤šà¥…à¤Ÿ à¤ªà¥à¤¨à¥à¤¹à¤¾ à¤‰à¤˜à¤¡à¤¾',
    tapOption: 'à¤ªà¥à¤¢à¥‡ à¤œà¤¾à¤£à¥à¤¯à¤¾à¤¸à¤¾à¤ à¥€ à¤ªà¤°à¥à¤¯à¤¾à¤¯à¤¾à¤µà¤° à¤Ÿà¥…à¤ª à¤•à¤°à¤¾',
    selectSubmit: 'à¤¨à¤¿à¤µà¤¡à¤¾ à¤†à¤£à¤¿ à¤¸à¤¬à¤®à¤¿à¤Ÿ à¤•à¤°à¤¾',
  },
  Gujarati: {
    closeTitle: 'àªšà«‡àªŸ àª¬àª‚àª§ àª•àª°à«‹?',
    closeBody: 'àª¤àª®àª¾àª°à«‹ àªšà«‡àªŸ àª‡àª¤àª¿àª¹àª¾àª¸ àª¸àª¾àª« àª¥àªˆ àªœàª¶à«‡ àª…àª¨à«‡ àª¤àª®à«‡ àª¨àªµà«€ àªµàª¾àª¤àªšà«€àª¤ àª¶àª°à«‚ àª•àª°àª¶à«‹. àª† àªªà«‚àª°à«àªµàªµàª¤à« àª•àª°à«€ àª¶àª•àª¾àª¶à«‡ àª¨àª¹à«€àª‚.',
    closeCancel: 'àª°àª¦ àª•àª°à«‹',
    closeConfirm: 'àªšà«‡àªŸ àª¬àª‚àª§ àª•àª°à«‹',
    preChatName: 'àª¶àª°à«‚ àª•àª°àª¤àª¾ àªªàª¹à«‡àª²àª¾, àª¤àª®àª¾àª°à«àª‚ àª¨àª¾àª® àª¶à«àª‚ àª›à«‡?',
    namePlaceholder: 'àª¤àª®àª¾àª°à«àª‚ àª¨àª¾àª® àª¦àª¾àª–àª² àª•àª°à«‹...',
    startChat: 'àªšà«‡àªŸ àª¶àª°à«‚ àª•àª°à«‹',
    closedTitle: 'àªµàª¾àª¤àªšà«€àª¤ àª¬àª‚àª§',
    closedBody: 'àª† àªšà«‡àªŸ àª¸àªªà«‹àª°à«àªŸ àªŸà«€àª® àª¦à«àªµàª¾àª°àª¾ àª¬àª‚àª§ àª•àª°àªµàª¾àª®àª¾àª‚ àª†àªµà«€ àª›à«‡.',
    reopen: 'àªšà«‡àªŸ àª«àª°à«€àª¥à«€ àª–à«‹àª²à«‹',
    tapOption: 'àªšàª¾àª²à« àª°àª¾àª–àªµàª¾ àª®àª¾àªŸà«‡ àªµàª¿àª•àª²à«àªª àªªàª° àªŸà«‡àªª àª•àª°à«‹',
    selectSubmit: 'àªªàª¸àª‚àª¦ àª•àª°à«‹ àª…àª¨à«‡ àª¸àª¬àª®àª¿àªŸ àª•àª°à«‹',
  },
  Tamil: {
    closeTitle: 'à®…à®°à®Ÿà¯à®Ÿà¯ˆà®¯à¯ˆ à®®à¯‚à®Ÿà®µà®¾?',
    closeBody: 'à®‰à®™à¯à®•à®³à¯ à®…à®°à®Ÿà¯à®Ÿà¯ˆ à®µà®°à®²à®¾à®±à¯ à®…à®´à®¿à®•à¯à®•à®ªà¯à®ªà®Ÿà¯à®®à¯ à®®à®±à¯à®±à¯à®®à¯ à®¨à¯€à®™à¯à®•à®³à¯ à®ªà¯à®¤à®¿à®¯ à®‰à®°à¯ˆà®¯à®¾à®Ÿà®²à¯ˆà®¤à¯ à®¤à¯Šà®Ÿà®™à¯à®•à¯à®µà¯€à®°à¯à®•à®³à¯. à®‡à®¤à¯ˆ à®®à¯€à®³à®®à¯à®Ÿà®¿à®¯à®¾à®¤à¯.',
    closeCancel: 'à®°à®¤à¯à®¤à¯à®šà¯†à®¯à¯',
    closeConfirm: 'à®…à®°à®Ÿà¯à®Ÿà¯ˆà®¯à¯ˆ à®®à¯‚à®Ÿà¯',
    preChatName: 'à®¤à¯Šà®Ÿà®™à¯à®•à¯à®µà®¤à®±à¯à®•à¯ à®®à¯à®©à¯, à®‰à®™à¯à®•à®³à¯ à®ªà¯†à®¯à®°à¯ à®Žà®©à¯à®©?',
    namePlaceholder: 'à®‰à®™à¯à®•à®³à¯ à®ªà¯†à®¯à®°à¯ˆ à®‰à®³à¯à®³à®¿à®Ÿà®µà¯à®®à¯...',
    startChat: 'à®…à®°à®Ÿà¯à®Ÿà¯ˆà®¯à¯ˆà®¤à¯ à®¤à¯Šà®Ÿà®™à¯à®•à¯',
    closedTitle: 'à®‰à®°à¯ˆà®¯à®¾à®Ÿà®²à¯ à®®à¯‚à®Ÿà®ªà¯à®ªà®Ÿà¯à®Ÿà®¤à¯',
    closedBody: 'à®‡à®¨à¯à®¤ à®…à®°à®Ÿà¯à®Ÿà¯ˆ à®†à®¤à®°à®µà¯ à®•à¯à®´à¯à®µà®¾à®²à¯ à®®à¯‚à®Ÿà®ªà¯à®ªà®Ÿà¯à®Ÿà¯à®³à¯à®³à®¤à¯.',
    reopen: 'à®…à®°à®Ÿà¯à®Ÿà¯ˆà®¯à¯ˆ à®®à¯€à®£à¯à®Ÿà¯à®®à¯ à®¤à®¿à®±',
    tapOption: 'à®¤à¯Šà®Ÿà®° à®µà®¿à®°à¯à®ªà¯à®ªà®¤à¯à®¤à¯ˆà®¤à¯ à®¤à®Ÿà¯à®Ÿà®µà¯à®®à¯',
    selectSubmit: 'à®¤à¯‡à®°à¯à®¨à¯à®¤à¯†à®Ÿà¯à®¤à¯à®¤à¯ à®šà®®à®°à¯à®ªà¯à®ªà®¿à®•à¯à®•à®µà¯à®®à¯',
  },
  Telugu: {
    closeTitle: 'à°šà°¾à°Ÿà± à°®à±‚à°¸à°¿à°µà±‡à°¯à°¾à°²à°¾?',
    closeBody: 'à°®à±€ à°šà°¾à°Ÿà± à°šà°°à°¿à°¤à±à°° à°•à±à°²à°¿à°¯à°°à± à°šà±‡à°¯à°¬à°¡à±à°¤à±à°‚à°¦à°¿ à°®à°°à°¿à°¯à± à°®à±€à°°à± à°•à±Šà°¤à±à°¤ à°¸à°‚à°­à°¾à°·à°£à°¨à± à°ªà±à°°à°¾à°°à°‚à°­à°¿à°¸à±à°¤à°¾à°°à±. à°‡à°¦à°¿ à°°à°¦à±à°¦à± à°šà±‡à°¯à°¡à°‚ à°¸à°¾à°§à±à°¯à°‚ à°•à°¾à°¦à±.',
    closeCancel: 'à°°à°¦à±à°¦à± à°šà±‡à°¯à°¿',
    closeConfirm: 'à°šà°¾à°Ÿà± à°®à±‚à°¸à°¿à°µà±‡à°¯à°¿',
    preChatName: 'à°ªà±à°°à°¾à°°à°‚à°­à°¿à°‚à°šà±‡ à°®à±à°‚à°¦à±, à°®à±€ à°ªà±‡à°°à± à°à°®à°¿à°Ÿà°¿?',
    namePlaceholder: 'à°®à±€ à°ªà±‡à°°à±à°¨à± à°¨à°®à±‹à°¦à± à°šà±‡à°¯à°‚à°¡à°¿...',
    startChat: 'à°šà°¾à°Ÿà± à°ªà±à°°à°¾à°°à°‚à°­à°¿à°‚à°šà°‚à°¡à°¿',
    closedTitle: 'à°¸à°‚à°­à°¾à°·à°£ à°®à±‚à°¸à°¿à°µà±‡à°¯à°¬à°¡à°¿à°‚à°¦à°¿',
    closedBody: 'à°ˆ à°šà°¾à°Ÿà± à°¸à°ªà±‹à°°à±à°Ÿà± à°Ÿà±€à°®à± à°¦à±à°µà°¾à°°à°¾ à°®à±‚à°¸à°¿à°µà±‡à°¯à°¬à°¡à°¿à°‚à°¦à°¿.',
    reopen: 'à°šà°¾à°Ÿà± à°®à°³à±à°²à±€ à°¤à±†à°°à°µà°‚à°¡à°¿',
    tapOption: 'à°•à±Šà°¨à°¸à°¾à°—à°¿à°‚à°šà°¡à°¾à°¨à°¿à°•à°¿ à°Žà°‚à°ªà°¿à°•à°¨à± à°¨à±Šà°•à±à°•à°‚à°¡à°¿',
    selectSubmit: 'à°Žà°‚à°šà±à°•à±à°¨à°¿ à°¸à°®à°°à±à°ªà°¿à°‚à°šà°‚à°¡à°¿',
  },
  Kannada: {
    closeTitle: 'à²šà²¾à²Ÿà³ à²®à³à²šà³à²šà²¬à³‡à²•à³†?',
    closeBody: 'à²¨à²¿à²®à³à²® à²šà²¾à²Ÿà³ à²‡à²¤à²¿à²¹à²¾à²¸à²µà²¨à³à²¨à³ à²…à²³à²¿à²¸à²²à²¾à²—à³à²¤à³à²¤à²¦à³† à²®à²¤à³à²¤à³ à²¨à³€à²µà³ à²¹à³Šà²¸ à²¸à²‚à²­à²¾à²·à²£à³†à²¯à²¨à³à²¨à³ à²ªà³à²°à²¾à²°à²‚à²­à²¿à²¸à³à²¤à³à²¤à³€à²°à²¿. à²‡à²¦à²¨à³à²¨à³ à²¹à²¿à²‚à²ªà²¡à³†à²¯à²²à³ à²¸à²¾à²§à³à²¯à²µà²¿à²²à³à²².',
    closeCancel: 'à²°à²¦à³à²¦à³à²®à²¾à²¡à²¿',
    closeConfirm: 'à²šà²¾à²Ÿà³ à²®à³à²šà³à²šà²¿',
    preChatName: 'à²ªà³à²°à²¾à²°à²‚à²­à²¿à²¸à³à²µ à²®à³Šà²¦à²²à³, à²¨à²¿à²®à³à²® à²¹à³†à²¸à²°à³‡à²¨à³?',
    namePlaceholder: 'à²¨à²¿à²®à³à²® à²¹à³†à²¸à²°à²¨à³à²¨à³ à²¨à²®à³‚à²¦à²¿à²¸à²¿...',
    startChat: 'à²šà²¾à²Ÿà³ à²ªà³à²°à²¾à²°à²‚à²­à²¿à²¸à²¿',
    closedTitle: 'à²¸à²‚à²­à²¾à²·à²£à³† à²®à³à²šà³à²šà²²à²¾à²—à²¿à²¦à³†',
    closedBody: 'à²ˆ à²šà²¾à²Ÿà³ à²…à²¨à³à²¨à³ à²¬à³†à²‚à²¬à²² à²¤à²‚à²¡à²¦à²¿à²‚à²¦ à²®à³à²šà³à²šà²²à²¾à²—à²¿à²¦à³†.',
    reopen: 'à²šà²¾à²Ÿà³ à²®à²°à³ à²¤à³†à²°à³†à²¯à²¿à²°à²¿',
    tapOption: 'à²®à³à²‚à²¦à³à²µà²°à²¿à²¸à²²à³ à²†à²¯à³à²•à³†à²¯à²¨à³à²¨à³ à²Ÿà³à²¯à²¾à²ªà³ à²®à²¾à²¡à²¿',
    selectSubmit: 'à²†à²¯à³à²•à³†à²®à²¾à²¡à²¿ à²®à²¤à³à²¤à³ à²¸à²²à³à²²à²¿à²¸à²¿',
  },
  Malayalam: {
    closeTitle: 'à´šà´¾à´±àµà´±àµ à´…à´Ÿà´¯àµà´•àµà´•à´£àµ‹?',
    closeBody: 'à´¨à´¿à´™àµà´™à´³àµà´Ÿàµ† à´šà´¾à´±àµà´±àµ à´¹à´¿à´¸àµà´±àµà´±à´±à´¿ à´®à´¾à´¯àµà´•àµà´•à´ªàµà´ªàµ†à´Ÿàµà´‚, à´¨à´¿à´™àµà´™àµ¾ à´’à´°àµ à´ªàµà´¤à´¿à´¯ à´¸à´‚à´­à´¾à´·à´£à´‚ à´†à´°à´‚à´­à´¿à´•àµà´•àµà´‚. à´‡à´¤àµ à´ªà´´à´¯à´ªà´Ÿà´¿à´¯à´¾à´•àµà´•à´¾àµ» à´•à´´à´¿à´¯à´¿à´²àµà´².',
    closeCancel: 'à´±à´¦àµà´¦à´¾à´•àµà´•àµà´•',
    closeConfirm: 'à´šà´¾à´±àµà´±àµ à´…à´Ÿà´¯àµà´•àµà´•àµà´•',
    preChatName: 'à´†à´°à´‚à´­à´¿à´•àµà´•àµà´¨àµà´¨à´¤à´¿à´¨àµ à´®àµà´®àµà´ªàµ, à´¨à´¿à´™àµà´™à´³àµà´Ÿàµ† à´ªàµ‡à´°àµ†à´¨àµà´¤à´¾à´£àµ?',
    namePlaceholder: 'à´¨à´¿à´™àµà´™à´³àµà´Ÿàµ† à´ªàµ‡à´°àµ à´¨àµ½à´•àµà´•...',
    startChat: 'à´šà´¾à´±àµà´±àµ à´†à´°à´‚à´­à´¿à´•àµà´•àµà´•',
    closedTitle: 'à´¸à´‚à´­à´¾à´·à´£à´‚ à´…à´Ÿà´šàµà´šàµ',
    closedBody: 'à´ˆ à´šà´¾à´±àµà´±àµ à´¸à´ªàµà´ªàµ‹àµ¼à´Ÿàµà´Ÿàµ à´Ÿàµ€à´‚ à´…à´Ÿà´šàµà´šà´¿à´°à´¿à´•àµà´•àµà´¨àµà´¨àµ.',
    reopen: 'à´šà´¾à´±àµà´±àµ à´µàµ€à´£àµà´Ÿàµà´‚ à´¤àµà´±à´•àµà´•àµà´•',
    tapOption: 'à´¤àµà´Ÿà´°àµà´¨àµà´¨à´¤à´¿à´¨àµ à´“à´ªàµà´·à´¨à´¿àµ½ à´Ÿà´¾à´ªàµà´ªàµ à´šàµ†à´¯àµà´¯àµà´•',
    selectSubmit: 'à´¤à´¿à´°à´žàµà´žàµ†à´Ÿàµà´¤àµà´¤àµ à´¸à´®àµ¼à´ªàµà´ªà´¿à´•àµà´•àµà´•',
  },
  Punjabi: {
    closeTitle: 'à¨šà©ˆà¨Ÿ à¨¬à©°à¨¦ à¨•à¨°à©‹?',
    closeBody: 'à¨¤à©à¨¹à¨¾à¨¡à¨¾ à¨šà©ˆà¨Ÿ à¨‡à¨¤à¨¿à¨¹à¨¾à¨¸ à¨¸à¨¾à¨«à¨¼ à¨¹à©‹ à¨œà¨¾à¨µà©‡à¨—à¨¾ à¨…à¨¤à©‡ à¨¤à©à¨¸à©€à¨‚ à¨‡à©±à¨• à¨¨à¨µà©€à¨‚ à¨—à©±à¨²à¨¬à¨¾à¨¤ à¨¸à¨¼à©à¨°à©‚ à¨•à¨°à©‹à¨—à©‡à¥¤ à¨‡à¨¸ à¨¨à©‚à©° à¨µà¨¾à¨ªà¨¸ à¨¨à¨¹à©€à¨‚ à¨²à¨¿à¨† à¨œà¨¾ à¨¸à¨•à¨¦à¨¾à¥¤',
    closeCancel: 'à¨°à©±à¨¦ à¨•à¨°à©‹',
    closeConfirm: 'à¨šà©ˆà¨Ÿ à¨¬à©°à¨¦ à¨•à¨°à©‹',
    preChatName: 'à¨¸à¨¼à©à¨°à©‚ à¨•à¨°à¨¨ à¨¤à©‹à¨‚ à¨ªà¨¹à¨¿à¨²à¨¾à¨‚, à¨¤à©à¨¹à¨¾à¨¡à¨¾ à¨¨à¨¾à¨® à¨•à©€ à¨¹à©ˆ?',
    namePlaceholder: 'à¨†à¨ªà¨£à¨¾ à¨¨à¨¾à¨® à¨¦à¨°à¨œ à¨•à¨°à©‹...',
    startChat: 'à¨šà©ˆà¨Ÿ à¨¸à¨¼à©à¨°à©‚ à¨•à¨°à©‹',
    closedTitle: 'à¨—à©±à¨²à¨¬à¨¾à¨¤ à¨¬à©°à¨¦',
    closedBody: 'à¨‡à¨¹ à¨šà©ˆà¨Ÿ à¨¸à¨¹à¨¾à¨‡à¨¤à¨¾ à¨Ÿà©€à¨® à¨¦à©à¨†à¨°à¨¾ à¨¬à©°à¨¦ à¨•à©€à¨¤à©€ à¨—à¨ˆ à¨¹à©ˆà¥¤',
    reopen: 'à¨šà©ˆà¨Ÿ à¨¦à©à¨¬à¨¾à¨°à¨¾ à¨–à©‹à¨²à©à¨¹à©‹',
    tapOption: 'à¨œà¨¾à¨°à©€ à¨°à©±à¨–à¨£ à¨²à¨ˆ à¨µà¨¿à¨•à¨²à¨ª `à¨¤à©‡ à¨Ÿà©ˆà¨ª à¨•à¨°à©‹',
    selectSubmit: 'à¨šà©à¨£à©‹ à¨…à¨¤à©‡ à¨œà¨®à©à¨¹à¨¾à¨‚ à¨•à¨°à©‹',
  },
};

const t = (lang, key) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS.English[key] || key;

// ═══════════════════════════════════════════════════════════════════
//  HELPER SUB-COMPONENTS — New UI bits for the mega redesign
// ═══════════════════════════════════════════════════════════════════

// Quick Action Toolbar — floating bar above input (🏠 🔄 ❓ 👤)
const QuickActionToolbar = ({ onHome, onRestart, onFAQ, onLiveAgent, headerColor, liveRequested, chatClosed }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
    px: 1, py: 0.8, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
    borderTop: '1px solid rgba(0,0,0,0.05)', borderBottom: '1px solid rgba(0,0,0,0.05)',
  }}>
    {[
      { icon: <HomeIcon sx={{ fontSize: 16 }} />, label: 'Home', action: onHome, color: headerColor },
      { icon: <ReplayIcon sx={{ fontSize: 16 }} />, label: 'Restart', action: onRestart, color: '#6b7280' },
      { icon: <HelpOutlineIcon sx={{ fontSize: 16 }} />, label: 'Help', action: onFAQ, color: '#0ea5e9' },
      { icon: <SupportAgentIcon sx={{ fontSize: 16 }} />, label: liveRequested ? (chatClosed ? 'Live (closed)' : 'Live') : 'Agent', action: onLiveAgent, color: liveRequested ? '#e65100' : '#10b981', live: true },
    ].map((b, i) => (
      <Box key={i} onClick={b.action} sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, cursor: 'pointer',
        px: 1.5, py: 0.5, borderRadius: 1.5, transition: 'all 0.2s',
        color: b.color,
        '&:hover': { bgcolor: `${b.color}12`, transform: 'translateY(-1px)' },
        position: 'relative',
      }}>
        {b.live && liveRequested && (
          <FiberManualRecordIcon sx={{ position: 'absolute', top: 4, right: 6, fontSize: 8, color: '#10b981' }} />
        )}
        {b.icon}
        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{b.label}</Typography>
      </Box>
    ))}
  </Box>
);

// Smart suggestion chips — context-aware shortcuts above input
const SmartSuggestionBar = ({ suggestions = [], onPick, headerColor }) => {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <Box sx={{
      display: 'flex', gap: 0.5, overflowX: 'auto', px: 2, py: 0.8,
      bgcolor: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(6px)',
      borderTop: '1px solid rgba(0,0,0,0.04)',
      '&::-webkit-scrollbar': { display: 'none' },
      scrollbarWidth: 'none',
    }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, alignSelf: 'center', mr: 0.5, flexShrink: 0 }}>💡</Typography>
      {suggestions.map((s, i) => (
        <Chip key={i} label={s} size="small" onClick={() => onPick(s)}
          sx={{
            bgcolor: `${headerColor}10`, color: headerColor, fontWeight: 600, fontSize: 11, height: 26,
            border: `1px solid ${headerColor}30`, cursor: 'pointer',
            '&:hover': { bgcolor: `${headerColor}20`, transform: 'translateY(-1px)', boxShadow: `0 3px 8px ${headerColor}20` },
            transition: 'all 0.2s', flexShrink: 0,
          }}
        />
      ))}
    </Box>
  );
};

// Generic fallback articles — used only when the bot owner hasn't configured a Help Center.
// Kept industry-neutral so they make sense for any business (no payment/shipping assumptions).
const DEFAULT_HELP_ARTICLES = [
  { icon: '💬', title: 'How can I get help?', preview: 'Ask me anything below', body: "Just type your question in the chat and I'll do my best to help. If I can't answer, you can always talk to a human." },
  { icon: '⏰', title: 'Business hours', preview: 'When we are available', body: 'Our team is typically available during regular business hours. Leave a message anytime and we will get back to you.' },
  { icon: '🛡️', title: 'Privacy & security', preview: 'Your data is safe', body: 'Your conversations and personal details are kept private and secure. We only use your information to help with your request.' },
  { icon: '📞', title: 'Contact support', preview: 'Reach a human', body: "Need to speak with someone? Tap “Talk to a human” below and we'll connect you with our team." },
];

// FAQ / Help side panel
const FAQPanel = ({ open, onClose, headerColor, botSettings = {} }) => {
  const [query, setQuery] = useState('');
  const [openArticle, setOpenArticle] = useState(null);

  // Articles come from the bot owner's configured Help Center; fall back to generic defaults.
  const configured = Array.isArray(botSettings.helpCenter) ? botSettings.helpCenter : [];
  const articles = (configured.length > 0
    ? configured.filter(a => a && a.enabled !== false && (a.title || '').trim())
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : DEFAULT_HELP_ARTICLES);

  const filtered = query
    ? articles.filter(a =>
      (a.title || '').toLowerCase().includes(query.toLowerCase()) ||
      (a.preview || '').toLowerCase().includes(query.toLowerCase()) ||
      (a.body || '').toLowerCase().includes(query.toLowerCase()))
    : articles;

  // Reset the open article whenever the drawer closes
  useEffect(() => { if (!open) setOpenArticle(null); }, [open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, bgcolor: '#fafafa' } }}>
      <Box sx={{ p: 2, background: `linear-gradient(135deg, ${headerColor}, ${headerColor}dd)`, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {openArticle ? (
              <IconButton onClick={() => setOpenArticle(null)} size="small" sx={{ color: '#fff', ml: -1 }}><ChevronLeftIcon /></IconButton>
            ) : <HelpOutlineIcon />}
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{openArticle ? 'Help Center' : 'Help Center'}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        {!openArticle && (
          <TextField fullWidth size="small" placeholder="Search articles..."
            value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} /> }}
            sx={{ bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 12, bgcolor: '#fff' } }}
          />
        )}
      </Box>
      {openArticle ? (
        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ fontSize: 30 }}>{openArticle.icon || '📄'}</Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1f2937' }}>{openArticle.title}</Typography>
          </Box>
          <Typography sx={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {openArticle.body?.trim() || openArticle.preview || 'No details available for this article yet.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
              <Typography sx={{ fontSize: 32, mb: 1 }}>🔍</Typography>
              <Typography sx={{ fontSize: 12 }}>No articles match "{query}"</Typography>
            </Box>
          ) : filtered.map((a, i) => (
            <Box key={i} onClick={() => setOpenArticle(a)} sx={{
              p: 1.5, mb: 1, bgcolor: '#fff', borderRadius: 2, cursor: 'pointer',
              border: '1px solid #f0f0f0', display: 'flex', gap: 1.5, alignItems: 'center',
              transition: 'all 0.2s', '&:hover': { borderColor: headerColor, transform: 'translateX(2px)', boxShadow: `0 4px 12px ${headerColor}15` },
            }}>
              <Box sx={{ fontSize: 22, flexShrink: 0 }}>{a.icon || '📄'}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>{a.title}</Typography>
                <Typography sx={{ fontSize: 10.5, color: '#6b7280', mt: 0.2 }}>{a.preview}</Typography>
              </Box>
              <ArticleIcon sx={{ fontSize: 14, color: '#d1d5db' }} />
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0', bgcolor: '#fff', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 10, color: '#6b7280' }}>Can't find what you need?</Typography>
        <Button size="small" variant="text" sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: headerColor, mt: 0.5 }}
          onClick={() => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('talktokam-request-live')), 100); }}>
          Talk to a human →
        </Button>
      </Box>
    </Drawer>
  );
};

// Hamburger side menu — bot info + actions
const HamburgerMenu = ({ open, onClose, headerColor, botSettings, userName, onRestart, onHome, onFAQ, onTransfer, onCloseChat, liveRequested }) => (
  <Drawer anchor="left" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '85%', sm: 300 }, bgcolor: '#fff' } }}>
    <Box sx={{ background: `linear-gradient(135deg, ${headerColor}, ${headerColor}dd)`, color: '#fff', p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {(botSettings.companyLogo || botSettings.avatar) ? (
          <img src={botSettings.companyLogo || botSettings.avatar} alt="logo"
            onError={(e) => { e.target.style.display = 'none'; const initial = (botSettings.botName || 'C')[0].toUpperCase().replace(/[<>"'&]/g, ''); e.target.parentElement.innerHTML = '<span style=\"width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700;color:#fff\">' + initial + '</span>'; }}
            style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
        ) : (
          <Avatar sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 18, fontWeight: 700 }}>
            {(botSettings.botName || 'C')[0].toUpperCase()}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>{botSettings.botName || 'Chatbot'}</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85, mt: 0.3 }}>{userName ? `👤 ${userName}` : 'Guest'}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
    </Box>

    <List sx={{ flex: 1, p: 1.5 }} dense disablePadding>
      {[
        { icon: <HomeIcon />, label: 'Home', action: () => { onHome(); onClose(); }, color: headerColor },
        { icon: <ReplayIcon />, label: 'Restart conversation', action: () => { onRestart(); onClose(); }, color: '#6b7280' },
        { icon: <HelpOutlineIcon />, label: 'Help & FAQ', action: () => { onFAQ(); onClose(); }, color: '#0ea5e9' },
        { icon: <SupportAgentIcon />, label: liveRequested ? 'Live agent (active)' : 'Talk to live agent', action: () => { onTransfer(); onClose(); }, color: '#10b981' },
      ].map((it, i) => (
        <ListItemButton key={i} onClick={it.action} sx={{ borderRadius: 2, mb: 0.4, '&:hover': { bgcolor: `${it.color}10` } }}>
          <ListItemIcon sx={{ minWidth: 36, color: it.color }}>{it.icon}</ListItemIcon>
          <ListItemText primary={it.label} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
        </ListItemButton>
      ))}
    </List>

    <Box sx={{ p: 1.5, borderTop: '1px solid #f0f0f0' }}>
      <Button onClick={onCloseChat} fullWidth variant="outlined" color="error" size="small" sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 700 }}>
        End chat
      </Button>
    </Box>
  </Drawer>
);

// Quick emoji popover for input
const EmojiPicker = ({ anchorEl, onClose, onPick }) => {
  const emojis = ['😊', '👍', '❤️', '😂', '🎉', '🙏', '👏', '🔥', '✨', '💯', '😍', '🤔', '👋', '🙌', '💪', '😎', '🤝', '✅', '❌', '⭐', '💡', '📌', '🚀', '💬'];
  return (
    <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 280, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.3 }}>
        {emojis.map((e, i) => (
          <Box key={i} onClick={() => { onPick(e); onClose(); }} sx={{
            fontSize: 20, cursor: 'pointer', p: 0.5, borderRadius: 1, textAlign: 'center', transition: 'all 0.15s',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', transform: 'scale(1.25)' },
          }}>{e}</Box>
        ))}
      </Box>
    </Popover>
  );
};

// ═══════════════════════════════════════════════════════════════════

const UserMessage = () => {
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const inlineMode = searchParams.get('mode') === 'inline';

  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = getOrCreateSid(chatId);
  // Always read from ref to avoid stale closure after reset
  // sessionIdRef.current replaced with sessionIdRef.current directly

  const [botSettings, setBotSettings] = useState({});
  const [flow, setFlow] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [ratingHover, setRatingHover] = useState(null);
  const [liveRequested, setLiveRequested] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [chatClosed, setChatClosed] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [agentName, setAgentName] = useState(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [messageSeen, setMessageSeen] = useState(() => localStorage.getItem('talktokam_seen_' + chatId) === '1');
  const seenAtRef = useRef(null);
  const hasPendingMessageRef = useRef(localStorage.getItem('talktokam_pending_' + chatId) === '1');
  const agentNotifiedRef = useRef(false);
  const [reopening, setReopening] = useState(false);
  const [deactivated, setDeactivated] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const [showLiveSuggestion, setShowLiveSuggestion] = useState(false);

  const [dropdownVal, setDropdownVal] = useState('');
  const [otherText, setOtherText] = useState('');
  const [pendingSkip, setPendingSkip] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [shake, setShake] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  // Pre-chat state
  const [preChatDone, setPreChatDone] = useState(false);
  const [userName, setUserName] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [startingChat, setStartingChat] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentSlot, setAppointmentSlot] = useState('');
  const [appointmentName, setAppointmentName] = useState('');
  const [appointmentEmail, setAppointmentEmail] = useState('');
  const [otpExpected, setOtpExpected] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpDigits, setOtpDigits] = useState([]);
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [multiSelected, setMultiSelected] = useState([]);
  const [calendlySlots, setCalendlySlots] = useState([]);
  const [calendlyLoading, setCalendlyLoading] = useState(false);

  // ─── New UI state (mega redesign) ───
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [pendingLiveAgent, setPendingLiveAgent] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);
  const [reactions, setReactions] = useState({});
  const [copiedMsgIdx, setCopiedMsgIdx] = useState(null);

  const chatBodyRef = useRef(null);
  const initializedRef = useRef(false);
  const phoneInputRef = useRef(null);
  const livePollingRef = useRef(null);
  const statusPollingRef = useRef(null);
  const stepRef = useRef(step);
  const shuffledRef = useRef(null);
  const isAdvancingRef = useRef(false);

  // Fetch Calendly slots when date changes
  useEffect(() => {
    if (!appointmentDate) { setCalendlySlots([]); return; }
    const apiBase = AUTH_API.replace('/api/auth', '');
    setCalendlyLoading(true);
    axios.get(`${apiBase}/api/ai-agents/calendly/default-event-type`).then(infoRes => {
      if (!infoRes.data?.ok || !infoRes.data?.configured) {
        setCalendlyLoading(false);
        return;
      }
      const eventTypeUri = infoRes.data.eventTypeUri;
      return axios.get(`${apiBase}/api/ai-agents/calendly/slots`, {
        params: { eventTypeUri, date: appointmentDate },
      }).then(slotRes => {
        if (slotRes.data?.ok) {
          setCalendlySlots(slotRes.data.slots.map(s => {
            const d = new Date(s.dateTime);
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
          }));
        }
      });
    }).catch(() => {}).finally(() => setCalendlyLoading(false));
  }, [appointmentDate]);

  // Load bot settings on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const loadSettings = async () => {
      try {
        let botSettingsData = {};
        let flowData = [];
        try {
          const botRes = await axios.get(`${BOTS_API}/${chatId}/settings`);
          botSettingsData = botRes.data || {};
          const flowRes = await axios.get(`${BOTS_API}/${chatId}/flow`);
          flowData = flowRes.data?.question?.list || [];
        } catch (e) {
          console.error("UserMessage: failed to load settings/flow", e?.message || e);
          botSettingsData = {};
          flowData = [];
        }
        setBotSettings(botSettingsData);
        setFlow(flowData);

        if (botSettingsData.status === 'inactive') {
          setDeactivated(true);
          setLoading(false);
          return;
        }

        // Restore from localStorage only â€” no API call needed
        const saved = loadState(chatId);
        const flowList = flowData;

        if (saved && saved.preChatDone) {
          // Restore ANY state where user already entered their name
          const validStep = Math.min(saved.step || 0, flowList.length);
          setMessages(saved.messages || []);
          setPreChatDone(true);
          setUserName(saved.userName || '');
          setStep(validStep);
          setDone(saved.done || false);
          setLiveRequested(saved.liveRequested || false);
          setChatClosed(saved.chatClosed || false);

          // Live agent mode: sync latest messages from API silently
          if (saved.liveRequested && !saved.chatClosed) {
            axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`)
              .then(r => {
                if (r.data.ok) {
                  const msgs = (r.data.messages || []).map(m =>
                    (m.sender === 'admin' || m.sender === 'agent') && r.data.agentName
                      ? { ...m, agentName: r.data.agentName }
                      : m
                  );
                  setMessages(msgs);
                  if (r.data.agentName) setAgentName(r.data.agentName);
                  if (r.data.status === 'closed') setChatClosed(true);
                }
              })
              .catch(() => { });

            // Reconnect socket for agent-typing after refresh
            const sock = connectSocket();
            if (sock) {
              const doJoin = () => joinConversation(chatId, sessionIdRef.current);
              if (sock.connected) doJoin(); else sock.once('connect', doJoin);
              sock.off('agent-typing');
              sock.on('agent-typing', ({ chatbotId, sessionId }) => {
                if (chatbotId === chatId && sessionId === sessionIdRef.current) {
                  setAgentTyping(true);
                  setTimeout(() => setAgentTyping(false), 3000);
                }
              });
              sock.off('message-seen');
              sock.on('message-seen', ({ chatbotId, sessionId }) => {
                if (chatbotId === chatId && sessionId === sessionIdRef.current) {
                  seenAtRef.current = Date.now();
                  if (hasPendingMessageRef.current) { hasPendingMessageRef.current = false; localStorage.removeItem('talktokam_pending_' + chatId); setMessageSeen(true); localStorage.setItem('talktokam_seen_' + chatId, '1'); }
                }
              });
              sock.off('chat-resolved');
              sock.on('chat-resolved', ({ chatbotId, sessionId }) => {
                if (chatbotId === chatId && sessionId === sessionIdRef.current) setChatClosed(true);
              });
            }

            // Restart polling after refresh
            livePollingRef.current = setInterval(async () => {
              try {
                const res = await axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`);
                if (!res.data.ok) return;
                if (res.data.status === 'closed') { setChatClosed(true); clearInterval(livePollingRef.current); return; }
                const currentAgentName = res.data.agentName;
                const serverMsgs = res.data.messages || [];
                setMessages(prev => {
                  const localCount = prev.filter(m => !m._optimistic).length;
                  if (serverMsgs.length <= localCount) return prev;
                  setAgentTyping(false);
                  return serverMsgs.map(m =>
                    (m.sender === 'admin' || m.sender === 'agent') && currentAgentName
                      ? { ...m, agentName: currentAgentName } : m
                  );
                });
                if (currentAgentName) setAgentName(currentAgentName);
              } catch { }
            }, 2000);
          }
        }
      } catch (err) {
        console.error('Widget load error:', err);
        setBotSettings({});
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [chatId]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus name input on pre-chat screen
  useEffect(() => {
    if (!loading && !preChatDone && phoneInputRef.current) {
      setTimeout(() => phoneInputRef.current?.focus(), 300);
    }
  }, [loading, preChatDone]);

  // Poll status when chat is active (not live) â€” detect if admin closed the chat
  useEffect(() => {
    if (statusPollingRef.current) clearInterval(statusPollingRef.current);
    if (!preChatDone || liveRequested || chatClosed) return;

    statusPollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`);
        if (res.data.ok && res.data.status === 'closed') {
          setChatClosed(true);
          setMessages(prev => [...prev, {
            sender: 'bot',
            text: 'ðŸ”’ This conversation has been closed by the support team.',
          }]);
        }
      } catch { /* silent */ }
    }, 8000);

    return () => clearInterval(statusPollingRef.current);
  }, [preChatDone, liveRequested, chatClosed, chatId]);

  // Listen for live-agent request from FAQ panel / drawer
  useEffect(() => {
    const handler = () => handleRequestLive();
    window.addEventListener('talktokam-request-live', handler);
    return () => window.removeEventListener('talktokam-request-live', handler);
  }, []); // eslint-disable-line

  // Persist full session state (including messages) to localStorage
  useEffect(() => {
    if (preChatDone) {
      saveState(chatId, { preChatDone, userName, step, done, liveRequested, chatClosed, messages });
    }
  }, [preChatDone, userName, step, done, liveRequested, chatClosed, messages, chatId]);

  // Cleanup all polling on unmount
  useEffect(() => {
    return () => {
      if (livePollingRef.current) clearInterval(livePollingRef.current);
      if (statusPollingRef.current) clearInterval(statusPollingRef.current);
    };
  }, []);

  // Reset form values when step changes
  useEffect(() => {
    setFormValues({});
    setAppointmentDate('');
    setAppointmentSlot('');
    setOtpExpected('');
    setOtpSent(false);
    setOtpDigits([]);
    setOtpResendIn(0);
  }, [step]);

  // OTP resend countdown
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  const handleConfirmClose = () => {
    if (livePollingRef.current) clearInterval(livePollingRef.current);
    if (statusPollingRef.current) clearInterval(statusPollingRef.current);
    // Mark conversation as closed in DB (fire-and-forget)
    axios.post(`${CONV_API}/end`, { chatbotId: chatId, sessionId: sessionIdRef.current }).catch(() => { });
    clearSession(chatId);
    const newSid = 'sess_' + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(`talktokam_sid_${chatId}`, newSid);
    sessionIdRef.current = newSid;
    setMessages([]);
    setPreChatDone(false);
    setUserName('');
    setPhoneInput('');
    setStep(0);
    setDone(false);
    setLiveRequested(false);
    setChatClosed(false);
    setReopening(false);
    setConfirmClose(false);
    setShowLiveSuggestion(false);
  };

  const handleEndLiveChat = async () => {
    setConfirmClose(false);
    try {
      await axios.post(`${CONV_API}/end`, { chatbotId: chatId, sessionId: sessionIdRef.current });
    } catch {}
    if (livePollingRef.current) clearInterval(livePollingRef.current);
    if (statusPollingRef.current) clearInterval(statusPollingRef.current);
    setChatClosed(true);
  };

  const pushMessage = useCallback((sender, msgText, questionId = null, opts = {}) => {
    setMessages(prev => [...prev, { sender, text: msgText, questionId, isSkip: !!opts.isSkip, intent: opts.intent }]);
    axios.post(`${CONV_API}/message`, {
      chatbotId: chatId,
      sessionId: sessionIdRef.current,
      sender,
      text: msgText,
      questionId,
      isSkip: !!opts.isSkip,
      intent: opts.intent,
    }).catch(console.error);
  }, [chatId]);

  // Quick-action: skip name form, auto-start with intent + first message
  const handleQuickAction = async (intent) => {
    if (loading || startingChat) return;
    const name = phoneInput.trim();
    if (!name) {
      setPhoneError('Please enter your WhatsApp number first.');
      return;
    }
    if (!/^\d{10,15}$/.test(name)) {
      setPhoneError('Please enter a valid 10-15 digit WhatsApp number.');
      return;
    }
    setPhoneError('');
    setStartingChat(true);
    try {
      await axios.post(`${CONV_API}/init`, {
        chatbotId: chatId,
        botId: chatId,
        sessionId: sessionIdRef.current,
        flow,
        userName: name,
        intent,
      });
      setUserName(name);
      setPreChatDone(true);

      const hour = new Date().getHours();
      const greetingText = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : hour < 21 ? 'Good Evening' : 'Hey there';
      const timeEmoji = hour < 12 ? '🌅' : hour < 17 ? '☀️' : hour < 21 ? '🌆' : '🌙';
      const finalWelcome = (botSettings.welcomeText || `Hi {name}! How can I help you?`)
        .replace(/\{greeting\}/gi, greetingText)
        .replace(/\{time_emoji\}/gi, timeEmoji)
        .replace(/\{name\}/gi, name);

      // First user message describes the intent
      const intentText = {
        book: 'I want to book an appointment',
        shop: 'I want to see products',
        help: 'I need help with something',
      }[intent] || `Intent: ${intent}`;

      setMessages([
        { sender: 'bot', text: finalWelcome },
      ]);
      // Push intent message from user side after small delay
      setTimeout(() => {
        pushMessage('user', intentText, null, { intent });
        // Then start the flow
        setTimeout(() => {
          if (flow.length > 0) {
            const q = flow[0];
            pushMessage('bot', resolveReferences(q.text), q.id);
          }
        }, 500);
      }, 600);
    } catch (err) {
      console.error('Quick action error:', err);
      setPhoneError('Something went wrong. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  // Restart: clear state and re-push welcome + first question (keeps convo open)
  const handleRestartConversation = async () => {
    if (!preChatDone) return;
    setMenuOpen(false);
    setText('');
    setStep(0);
    setDone(false);
    setMessages([]);
    setFormValues({});
    setAppointmentDate('');
    setAppointmentSlot('');
    setOtpExpected('');
    setOtpSent(false);
    setOtpDigits([]);
    setOtpResendIn(0);
    setReactions({});
    setValidationError('');

    try {
      await axios.post(`${CONV_API}/init`, {
        chatbotId: chatId,
        botId: chatId,
        sessionId: sessionIdRef.current,
        flow,
        userName,
        intent: 'general',
      });
      const hour = new Date().getHours();
      const greetingText = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : hour < 21 ? 'Good Evening' : 'Hey there';
      const timeEmoji = hour < 12 ? '🌅' : hour < 17 ? '☀️' : hour < 21 ? '🌆' : '🌙';
      const finalWelcome = (botSettings.welcomeText || `Hi {name}! How can I help you?`)
        .replace(/\{greeting\}/gi, greetingText)
        .replace(/\{time_emoji\}/gi, timeEmoji)
        .replace(/\{name\}/gi, userName);
      setMessages([{ sender: 'bot', text: finalWelcome }]);
      if (flow.length > 0) {
        setTimeout(() => {
          const q = flow[0];
          pushMessage('bot', resolveReferences(q.text), q.id);
        }, 600);
      }
    } catch (err) {
      console.error('Restart error:', err);
    }
  };

  // End chat: close conversation API + clear all state + back to pre-chat
  const handleEndChat = async () => {
    setMenuOpen(false);
    try {
      await axios.post(`${CONV_API}/end`, { chatbotId: chatId, sessionId: sessionIdRef.current });
    } catch (err) {
      // Conversation may not exist (user never started) — that's fine
    }
    // Hard reset all state
    setText('');
    setStep(0);
    setDone(false);
    setMessages([]);
    setPreChatDone(false);
    setUserName('');
    setPhoneInput('');
    setPhoneError('');
    setLiveRequested(false);
    setLiveLoading(false);
    setChatClosed(false);
    setAgentName(null);
    setAgentTyping(false);
    setFormValues({});
    setAppointmentDate('');
    setAppointmentSlot('');
    setOtpExpected('');
    setOtpSent(false);
    setOtpDigits([]);
    setOtpResendIn(0);
    setReactions({});
    setShowLiveSuggestion(false);
    setValidationError('');
    setMenuOpen(false);
    setFaqOpen(false);
    // Clear localStorage so a fresh sessionId is generated
    if (typeof window !== 'undefined') {
      clearSession(chatId);
      localStorage.removeItem('talktokam_seen_' + chatId);
      localStorage.removeItem('talktokam_pending_' + chatId);
    }
    // Generate new session id for the next conversation
    if (typeof window !== 'undefined') {
      sessionIdRef.current = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    }
  };

  // Talk to live agent from hamburger: if pre-chat not done, ask for name first
  const handleTransferClick = () => {
    setMenuOpen(false);
    if (!preChatDone) {
      setPendingLiveAgent(true);
      setTimeout(() => phoneInputRef.current?.focus(), 100);
    } else {
      handleRequestLive();
    }
  };

  // Start chat as guest (no name required)
  const handleStartAsGuest = () => handleStartChat('Guest');

  const handleStartChat = async (overridePhone = null) => {
    const phone = (typeof overridePhone === 'string' ? overridePhone : phoneInput).trim();
    if (!phone) {
      setPhoneError('Please enter your WhatsApp number to continue.');
      return;
    }
    if (!/^\d{10,15}$/.test(phone)) {
      setPhoneError('Please enter a valid 10-15 digit WhatsApp number.');
      return;
    }
    if (loading) {
      setPhoneError('Still loading... please wait.');
      return;
    }
    setPhoneError('');
    setStartingChat(true);

    try {
      await axios.post(`${CONV_API}/init`, {
        chatbotId: chatId,
        botId: chatId,
        sessionId: sessionIdRef.current,
        flow,
        userName: phone,
        contactPhone: phone,
        intent: 'general',
      });

      setUserName(phone);
      setPreChatDone(true);

      const welcome = botSettings.welcomeText || `Hi ${phone}! How can I help you?`;
      const hour = new Date().getHours();
      const greetingText = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : hour < 21 ? 'Good Evening' : 'Hey there';
      const timeEmoji = hour < 12 ? '🌅' : hour < 17 ? '☀️' : hour < 21 ? '🌆' : '🌙';
      const finalWelcome = welcome
        .replace(/\{greeting\}/gi, greetingText)
        .replace(/\{time_emoji\}/gi, timeEmoji)
        .replace(/\{name\}/gi, phone);
      setMessages([{ sender: 'bot', text: finalWelcome }]);

      // Fetch server-side messages to check for greeting message
      try {
        const convoRes = await axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`);
        const serverMessages = convoRes.data?.messages || [];
        const serverGreeting = serverMessages.find(m => m.sender === 'bot' && m.text && m.text !== finalWelcome);
        if (serverGreeting) {
          setMessages([{ sender: 'bot', text: serverGreeting.text }, { sender: 'bot', text: finalWelcome }]);
        }
      } catch (e) {
        // ignore — use local welcome only
      }

      if (flow.length > 0) {
        setTimeout(() => {
          const q = flow[0];
          pushMessage('bot', resolveReferences(q.text), q.id);
        }, 700);
      }

      // If user clicked "Talk to live agent" from pre-chat, connect them now
      if (pendingLiveAgent) {
        setPendingLiveAgent(false);
        setTimeout(() => handleRequestLive(), 1000);
      }
    } catch (err) {
      console.error('Chat start error:', err);
      setPhoneError('Something went wrong. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  const handleRequestLive = async () => {
    setLiveLoading(true);
    try {
      await axios.post(`${CONV_API}/request-live`, {
        chatbotId: chatId,
        sessionId: sessionIdRef.current,
      });
      setLiveRequested(true);
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: "✅ A live agent has been notified. We'll connect you shortly!",
      }]);

      // Connect socket for real-time typing
      const socket = connectSocket();
      if (socket) {
        // Remove old listeners first to prevent duplicates
        socket.off('message-update');
        socket.off('agent-typing');
        socket.off('chat-resolved');
        socket.off('status-updated');
        socket.off('message-seen');

        const doJoin = () => joinConversation(chatId, sessionIdRef.current);
        if (socket.connected) {
          doJoin();
        } else {
          socket.once('connect', doJoin);
        }

        socket.on('chat-resolved', ({ chatbotId, sessionId }) => {
          if (chatbotId === chatId && sessionId === sessionIdRef.current) {
            setChatClosed(true);
          }
        });
        socket.on('status-updated', ({ chatbotId, sessionId, status }) => {
          if (chatbotId === chatId && sessionId === sessionIdRef.current && status === 'closed') {
            setChatClosed(true);
          }
        });
        socket.on('agent-typing', ({ chatbotId, sessionId }) => {
          if (chatbotId === chatId && sessionId === sessionIdRef.current) {
            setAgentTyping(true);
            setTimeout(() => setAgentTyping(false), 3000);
          }
        });
        socket.on('message-seen', ({ chatbotId, sessionId }) => {
          if (chatbotId === chatId && sessionId === sessionIdRef.current) {
            seenAtRef.current = Date.now();
            if (hasPendingMessageRef.current) { hasPendingMessageRef.current = false; localStorage.removeItem('talktokam_pending_' + chatId); setMessageSeen(true); localStorage.setItem('talktokam_seen_' + chatId, '1'); }
          }
        });
        socket.on('message-update', async ({ chatbotId, sessionId }) => {
          if (chatbotId !== chatId) return;
          // New message from agent — reset seen for new user messages
          seenAtRef.current = null;
          setMessageSeen(false);
          localStorage.removeItem(`talktokam_seen_${chatId}`);
          setAgentTyping(false);
          try {
            const res = await axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`);
            if (res.data.ok) {
              const currentAgentName = res.data.agentName;
              const serverMsgs = res.data.messages.map(m =>
                (m.sender === 'admin' || m.sender === 'agent') && currentAgentName
                  ? { ...m, agentName: currentAgentName } : m
              );
              // Add agent join notification if first time
              if (currentAgentName && !agentNotifiedRef.current) {
                agentNotifiedRef.current = true;
                setAgentName(currentAgentName);
                setMessages(serverMsgs);
              } else {
                setMessages(serverMsgs);
                if (currentAgentName) setAgentName(currentAgentName);
              }
            }
          } catch { }
        });
      }

      // Polling fallback — in case socket misses events
      livePollingRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${CONV_API}/session/${chatId}/${sessionIdRef.current}`);
          if (!res.data.ok) return;
          if (res.data.status === 'closed') {
            setChatClosed(true);
            clearInterval(livePollingRef.current);
            return;
          }
          const currentAgentName = res.data.agentName;
          const serverMsgs = res.data.messages || [];
          setMessages(prev => {
            const serverCount = serverMsgs.length;
            const localCount = prev.filter(m => !m._optimistic).length;
            if (serverCount <= localCount) return prev;
            setAgentTyping(false);
            return serverMsgs.map(m =>
              (m.sender === 'admin' || m.sender === 'agent') && currentAgentName
                ? { ...m, agentName: currentAgentName } : m
            );
          });
          if (currentAgentName) setAgentName(currentAgentName);
        } catch { }
      }, 2000);

    } catch (err) {
      console.error('Live request error:', err);
    } finally {
      setLiveLoading(false);
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await axios.post(`${CONV_API}/reopen`, {
        chatbotId: chatId,
        sessionId: sessionIdRef.current,
      });
      setChatClosed(false);
      setLiveRequested(false);
      if (livePollingRef.current) clearInterval(livePollingRef.current);
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: '✅ Chat has been reopened. Feel free to connect with a live agent again.',
      }]);
    } catch (err) {
      console.error('Reopen error:', err);
    } finally {
      setReopening(false);
    }
  };

  // Data Referencing — replace {Q1}, {name}, {email}, {phone} with previous answers
  const resolveReferences = (text) => {
    if (!text || !text.includes('{')) return text;
    let result = text;
    // {Q1}, {Q2}, etc. — answer to Nth question
    result = result.replace(/\{Q(\d+)\}/gi, (_, num) => {
      const idx = parseInt(num) - 1;
      const qId = flow[idx]?.id;
      if (!qId) return '';
      const ans = messages.find(m => m.sender === 'user' && m.questionId === qId);
      return ans?.text || '';
    });
    // {name} — answer to isUserName question, or pre-chat name
    result = result.replace(/\{name\}/gi, () => {
      const nameQ = flow.find(f => f.isUserName);
      if (nameQ) {
        const ans = messages.find(m => m.sender === 'user' && m.questionId === nameQ.id);
        if (ans?.text) return ans.text;
      }
      return userName || '';
    });
    // {email} — answer to email_feild type question
    result = result.replace(/\{email\}/gi, () => {
      const emailQ = flow.find(f => f.type === 'email_feild');
      if (!emailQ) return '';
      const ans = messages.find(m => m.sender === 'user' && m.questionId === emailQ.id);
      return ans?.text || '';
    });
    // {phone} — answer to mobile_number or number type question
    result = result.replace(/\{phone\}/gi, () => {
      const phoneQ = flow.find(f => f.type === 'mobile_number' || f.type === 'number');
      if (!phoneQ) return '';
      const ans = messages.find(m => m.sender === 'user' && m.questionId === phoneQ.id);
      return ans?.text || '';
    });
    return result;
  };

  const getValidationHint = (question) => {
    if (!question) return '';
    const { type, validations } = question;
    const hints = [];

    if (type === 'email_feild') hints.push('Valid email required (user@example.com)');
    if (type === 'number') hints.push('Numeric value only');
    if (type === 'mobile_number') hints.push('Digits only');
    if (type === 'website') hints.push('Valid URL (https://...)');
    if (type === 'location') hints.push('Enter your location');
    if (validations?.minLength != null) hints.push(`Min ${validations.minLength}${type === 'number' ? ' value' : ' chars'}`);
    if (validations?.maxLength != null) hints.push(`Max ${validations.maxLength}${type === 'number' ? ' value' : ' chars'}`);
    if (validations?.pattern) hints.push('Custom pattern required');

    return hints.join(' Â· ');
  };

  const validateAnswer = (answer, question) => {
    if (!question) return { valid: true };
    const { type, validations } = question;
    const msg = question.errorMessage || 'Please enter a valid answer';

    if (type === 'email_feild') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer)) {
        return { valid: false, error: msg };
      }
    }

    if (type === 'website') {
      if (!/^https?:\/\/.+\..+/.test(answer.trim())) {
        return { valid: false, error: msg || 'Please enter a valid URL (https://...)' };
      }
    }

    if (type === 'number') {
      if (answer.trim() === '' || isNaN(Number(answer))) {
        return { valid: false, error: msg };
      }
      const num = Number(answer);
      if (validations?.minLength != null && num < validations.minLength) {
        return { valid: false, error: msg };
      }
      if (validations?.maxLength != null && num > validations.maxLength) {
        return { valid: false, error: msg };
      }
    }

    if (type === 'mobile_number') {
      // Allow +, spaces and dashes; validate by digit count.
      const digits = answer.replace(/\D/g, '');
      const min = validations?.minLength != null ? validations.minLength : 7;
      const max = validations?.maxLength != null ? validations.maxLength : 15;
      if (digits.length < min || digits.length > max) {
        return { valid: false, error: msg };
      }
    }

    // Generic length checks apply to text-like answers only.
    // (number uses value range above, mobile_number uses digit count.)
    if (type !== 'number' && type !== 'mobile_number') {
      if (validations?.minLength != null && answer.length < validations.minLength) {
        return { valid: false, error: msg };
      }
      if (validations?.maxLength != null && answer.length > validations.maxLength) {
        return { valid: false, error: msg };
      }
    }
    if (validations?.pattern) {
      try {
        if (!new RegExp(validations.pattern).test(answer)) {
          return { valid: false, error: msg };
        }
      } catch { }
    }

    return { valid: true };
  };

  // Check conditions to determine next step
  const getNextStep = (currentStep, answer) => {
    const q = flow[currentStep];
    if (q?.conditions?.length > 0) {
      // Check for wildcard (*) first
      const wildcard = q.conditions.find(c => c.jumpTo && (c.answer === '*' || c.answer === 'any' || c.answer === 'all'));
      // Check for exact/partial match
      const match = q.conditions.find(c => c.answer && c.jumpTo && c.answer !== '*' && c.answer !== 'any' && c.answer !== 'all' && answer.toLowerCase().includes(c.answer.toLowerCase()));
      const found = match || wildcard;
      if (found) {
        const jumpIdx = flow.findIndex(f => f.id === found.jumpTo);
        if (jumpIdx >= 0) return jumpIdx;
      }
    }
    return currentStep + 1;
  };

  const withAdvanceGuard = (fn) => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    setTimeout(() => { isAdvancingRef.current = false; }, 900);
    fn();
  };

  const advanceStep = (nextStep) => {
    setDropdownVal('');
    setOtherText('');
    setPendingSkip(false);
    setValidationError('');
    setMultiSelected([]);
    setStep(nextStep);
    if (nextStep < flow.length) {
      setTimeout(() => {
        const q = flow[nextStep];
        pushMessage('bot', resolveReferences(q.text), q.id);
      }, 600);
    } else {
      setTimeout(() => {
        const hasLeadCapture = flow.some(q =>
          ['form', 'ask_contacts', 'email_feild', 'mobile_number', 'file_upload',
           'order_items', 'appointment_booking', 'authenticator'].includes(q.type)
        );
        if (hasLeadCapture) {
          setMessages(prev => [...prev, { sender: 'bot', text: "__FLOW_COMPLETE__" }]);
        }
        if (botSettings.aiEnabled) {
          const wt = botSettings.welcomeText;
          if (!wt || wt === 'Hey') {
            setTimeout(() => {
              setMessages(prev => [...prev, { sender: 'bot', text: "🤖 I'm your virtual assistant! If you have any other questions about our services, feel free to ask below. Or click 'Chat with Live Agent' to talk to a real person." }]);
            }, 800);
          }
        } else {
          setTimeout(() => {
            setMessages(prev => [...prev, { sender: 'bot', text: "✅ Connecting you with a live agent. Please wait a moment..." }]);
            handleRequestLive();
          }, 600);
        }
        setDone(true);
      }, 600);
    }
  };

  const handleOptionSelect = (optionText) => {
    if (done || liveRequested) return;
    withAdvanceGuard(() => {
      const currentQ = flow[step];
      pushMessage('user', optionText, currentQ?.id);
      advanceStep(getNextStep(step, optionText));
    });
  };

  const handleSkip = () => {
    if (done || liveRequested) return;
    withAdvanceGuard(() => {
      const currentQ = flow[step];
      if (currentQ?.required) return;
      pushMessage('user', '⏭ Skipped', currentQ?.id, { isSkip: true });
      advanceStep(step + 1);
    });
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    const userText = text.trim();
    setText('');

    // Live mode â€” user replying to admin
    if (liveRequested && !chatClosed) {
      setMessages(prev => [...prev, { sender: 'user', text: userText, _optimistic: true }]);
      setMessageSeen(false);
      localStorage.removeItem('talktokam_seen_' + chatId);
      hasPendingMessageRef.current = true;
      localStorage.setItem('talktokam_pending_' + chatId, '1');
      axios.post(`${CONV_API}/message`, {
        chatbotId: chatId,
        sessionId: sessionIdRef.current,
        sender: 'user',
        text: userText,
      }).catch(console.error);
      return;
    }

    if (done) {
      // AI mode — if enabled, send to AI endpoint
      if (botSettings.aiEnabled && !liveRequested) {
        setMessages(prev => [...prev, { sender: 'user', text: userText }]);

        setAiTyping(true);
        try {
          const aiRes = await axios.post(`${CONV_API}/ai-message`, {
            chatbotId: chatId,
            sessionId: sessionIdRef.current,
            text: userText,
          });
          setAiTyping(false);
          if (aiRes.data?.reply) {
            setMessages(prev => [...prev, { sender: 'bot', text: aiRes.data.reply }]);
            // Show live suggestion only if backend returned transfer message
            if (aiRes.data.reply.includes('🔄') || aiRes.data.reply.includes('Connect with Live Agent')) {
              setShowLiveSuggestion(true);
            }
          }
        } catch (err) {
          setAiTyping(false);
          const errMsg = err?.response?.data?.message || 'AI is unavailable right now.';
          setMessages(prev => [...prev, { sender: 'bot', text: errMsg, isError: true }]);
        }
      }
      return;
    }

    // No active question + AI enabled → route to AI immediately
    if (preChatDone && botSettings.aiEnabled && !liveRequested && !flow[step]) {
      setMessages(prev => [...prev, { sender: 'user', text: userText }]);
      setAiTyping(true);
      try {
        const aiRes = await axios.post(`${CONV_API}/ai-message`, {
          chatbotId: chatId,
          sessionId: sessionIdRef.current,
          text: userText,
        });
        setAiTyping(false);
        if (aiRes.data?.reply) {
          setMessages(prev => [...prev, { sender: 'bot', text: aiRes.data.reply }]);
          if (aiRes.data.reply.includes('🔄') || aiRes.data.reply.includes('Connect with Live Agent')) {
            setShowLiveSuggestion(true);
          }
        }
      } catch (err) {
        setAiTyping(false);
        const errMsg = err?.response?.data?.message || 'AI is unavailable right now.';
        setMessages(prev => [...prev, { sender: 'bot', text: errMsg, isError: true }]);
      }
      setDone(true);
      return;
    }

    const currentQ = flow[step];
    const result = validateAnswer(userText, currentQ);
    if (!result.valid) {
      setText(userText);
      setValidationError(result.error);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setValidationError('');
    pushMessage('user', userText, currentQ?.id);
    advanceStep(getNextStep(step, userText));
  };

  // Attach a file from the footer bar: upload (Cloudinary if the question
  // configures it, else inline base64) and post it as the visitor's message.
  const handleAttachFile = async (file) => {
    if (!file) return;
    const maxMb = 10;
    if (file.size > maxMb * 1024 * 1024) {
      setValidationError(`File too large. Max ${maxMb}MB.`);
      return;
    }
    setValidationError('');
    const isImage = file.type.startsWith('image/');
    const sendAsMessage = (msgText) => {
      if (liveRequested && !chatClosed) {
        setMessages(prev => [...prev, { sender: 'user', text: msgText, _optimistic: true }]);
        setMessageSeen(false);
        localStorage.removeItem('talktokam_seen_' + chatId);
        hasPendingMessageRef.current = true;
        localStorage.setItem('talktokam_pending_' + chatId, '1');
        axios.post(`${CONV_API}/message`, {
          chatbotId: chatId,
          sessionId: sessionIdRef.current,
          sender: 'user',
          text: msgText,
        }).catch(console.error);
      } else {
        setMessages(prev => [...prev, { sender: 'user', text: msgText }]);
      }
    };

    try {
      const url = await uploadViaBackend(file);
      sendAsMessage(isImage ? `[IMG]${url}` : `📎 ${file.name} ${url}`);
    } catch {
      setValidationError('Upload failed. Please try again.');
    }
  };

  // Voice input: transcribe speech into the input box via the Web Speech API.
  const handleMicToggle = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setValidationError('Voice input is not supported in this browser. Try Chrome.');
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = botSettings.speechLang || 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    let finalText = '';
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setValidationError('');
      setText((finalText + interim).trim());
    };
    rec.onend = () => { setListening(false); recognitionRef.current = null; };
    rec.onerror = () => { setListening(false); recognitionRef.current = null; };
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  const colors = botSettings.themeColors || {};
  const headerColor = colors.header || '#006C74';
  const answerColor = colors.answer || '#007bff';
  const questionColor = colors.question || '#ffffff';
  const chatBg = colors.chatBackground || '#f5f5f5';
  const optionColor = colors.option || '#007bff';
  const optionBorderColor = colors.optionBorder || '#444c5c';
  const isLightQuestion = questionColor === '#ffffff' || questionColor === '#fff';
  const bubbleStyle = botSettings.selectedBubbleStyle || 'style1';
  const borderRadius = botSettings.borderRadius != null ? Number(botSettings.borderRadius) : 10;
  const textAlign = botSettings.textAlign || 'left';
  const fontSize = botSettings.fontSize || '14px';
  const overlayOpacity = botSettings.overlayOpacity || 0;
  const displayLogo = botSettings.companyLogo || botSettings.avatar || 'https://cdn-icons-png.flaticon.com/512/4712/4712027.png';
  const avatarUrl = botSettings.avatar || botSettings.companyLogo || '';

  const getBubbleRadius = (sender) => {
    switch (bubbleStyle) {
      case 'style2': return '20px';
      case 'style3': return sender === 'user' ? '12px 12px 0px 12px' : '12px 12px 12px 0px';
      case 'style4': return sender === 'user' ? '12px 12px 12px 0px' : '12px 12px 0px 12px';
      default: return sender === 'user' ? '8px 8px 0px 8px' : '8px 8px 8px 0px';
    }
  };

  const getOptionLabel = (opt) => typeof opt === 'string' ? opt : (opt?.label || opt?.value || String(opt));

  const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const currentQ = flow[step];
  if (stepRef.current !== step) {
    shuffledRef.current = null;
    stepRef.current = step;
  }
  if (currentQ?.shuffleOptions && !shuffledRef.current) {
    shuffledRef.current = shuffleArray(currentQ.options);
  }
  if (!currentQ?.shuffleOptions) shuffledRef.current = null;
  const displayOptions = shuffledRef.current || currentQ?.options || [];
  const visibleOptions = (displayOptions || []).filter(opt => !(typeof opt === 'object' && opt?.hidden));
  const hasOtherOption = currentQ?.otherOption;
  const isDropdown = currentQ?.style === 'dropdown';
  const isList = currentQ?.style === 'list';
  const layoutDir = currentQ?.flexDirection || 'row';

  const questionSpoken = messages.some(m => m.sender === 'bot' && m.questionId === currentQ?.id);
  const hasOptions = !done && !liveRequested && questionSpoken && (visibleOptions?.length > 0) && !['multiple_options', 'carousel_with_buttons', 'order_items', 'form', 'ask_contacts', 'appointment_booking', 'authenticator'].includes(currentQ?.type);

  if (deactivated) {
    return (
      <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', p: 3 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <SmartToyIcon sx={{ fontSize: 32, color: '#ef4444' }} />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1f2937', mb: 1 }}>This chatbot is currently deactivated</Typography>
        <Typography sx={{ fontSize: 14, color: '#6b7280', textAlign: 'center', maxWidth: 300 }}>
          The owner has disabled this bot. Please check back later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%', height: inlineMode ? '100%' : '100vh',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: botSettings.font || 'inherit',
      fontSize,
    }}>
      {/* Header */}
      <Box sx={{ bgcolor: headerColor, color: '#fff', px: 1.2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          {/* Avatar with elastic mount + gentle nodding idle */}
          <Box sx={{
            position: 'relative',
            animation: 'talktokam-enter-sm 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both, talktokam-nod-sm 4.5s ease-in-out 0.7s infinite',
            '@keyframes talktokam-enter-sm': {
              '0%': { transform: 'scale(0.3) rotate(-90deg)', opacity: 0 },
              '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
            },
            '@keyframes talktokam-nod-sm': {
              '0%, 100%': { transform: 'rotate(0deg) scale(1)' },
              '25%': { transform: 'rotate(-3deg) scale(1.02)' },
              '75%': { transform: 'rotate(3deg) scale(1.02)' },
            },
          }}>
            {displayLogo && !logoError ? (
              <img
                src={displayLogo}
                alt="logo"
                onError={() => setLogoError(true)}
                style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <Avatar sx={{ width: 38, height: 38, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 16, fontWeight: 700 }}>
                {(botSettings.botName || 'C')[0].toUpperCase()}
              </Avatar>
            )}
          </Box>
          {liveRequested && <FiberManualRecordIcon sx={{ position: 'absolute', bottom: -1, right: -1, fontSize: 12, color: '#fbbf24', bgcolor: headerColor, borderRadius: '50%', p: '1px', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={14} noWrap sx={{ letterSpacing: -0.2 }}>{botSettings.botName || 'Chatbot'}</Typography>
          {botSettings.description ? (
            <Typography
              fontSize={10.5}
              sx={{ opacity: 0.85, lineHeight: 1.2, mt: 0.1 }}
              noWrap
            >
              {botSettings.description}
            </Typography>
          ) : (
            /* Status line — only when no description and something interesting is happening */
            (liveRequested || agentTyping) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FiberManualRecordIcon sx={{ fontSize: 7, color: liveRequested ? '#fbbf24' : '#60a5fa' }} />
                <Typography fontSize={10.5} sx={{ opacity: 0.9, fontWeight: 700 }} noWrap>
                  {liveRequested ? (chatClosed ? 'Agent left' : `${agentName || 'Agent'} connected • live`) : (agentTyping ? 'typing…' : '')}
                </Typography>
              </Box>
            )
          )}
        </Box>
        {preChatDone && userName && (
          <Box sx={{ px: 1, py: 0.3, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '12px', flexShrink: 0 }}>
            <Typography fontSize={11} fontWeight={600} noWrap sx={{ maxWidth: 80 }}>👤 {userName}</Typography>
          </Box>
        )}
        {!inlineMode && (
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Minimize" placement="bottom">
              <Box onClick={() => window.parent.postMessage({ type: 'TALKTOKAM_MINIMIZE' }, window.location.origin)}
                sx={{
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, transition: 'all 0.2s'
                }}>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
            </Tooltip>
            <Tooltip title="Close" placement="bottom">
              <Box onClick={() => setConfirmClose(true)}
                sx={{
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(239,68,68,0.3)' }, transition: 'all 0.2s'
                }}>
                <CloseIcon sx={{ fontSize: 14, color: '#fff' }} />
              </Box>
            </Tooltip>
            <Tooltip title="Menu" placement="bottom">
              <Box onClick={() => setMenuOpen(true)}
                sx={{
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, transition: 'all 0.2s'
                }}>
                <MenuIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Confirm close dialog */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 280 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
          {liveRequested ? 'End Live Chat?' : t(botSettings.defaultLanguage, 'closeTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {liveRequested ? 'Are you sure you want to end this live chat? A rating prompt will appear after.' : t(botSettings.defaultLanguage, 'closeBody')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmClose(false)} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
            {t(botSettings.defaultLanguage, 'closeCancel')}
          </Button>
          <Button onClick={liveRequested ? handleEndLiveChat : handleConfirmClose} variant="contained" color="error" size="small" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
            {liveRequested ? 'End Chat' : t(botSettings.defaultLanguage, 'closeConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€â”€ PRE-CHAT SCREEN â”€â”€â”€ */}
      {!preChatDone ? (
        <Box sx={{ background: chatBg, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'auto', p: 3 }}>
          {/* Simple hero */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative', display: 'inline-block', mx: 'auto' }}>
              {/* Avatar with elastic bounce-in mount + gentle nodding idle */}
              <Box sx={{
                position: 'relative',
                animation: 'talktokam-enter 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) both, talktokam-nod 5s ease-in-out 0.9s infinite',
                '@keyframes talktokam-enter': {
                  '0%': { transform: 'scale(0.2) rotate(-180deg)', opacity: 0 },
                  '60%': { transform: 'scale(1.12) rotate(8deg)', opacity: 1 },
                  '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
                },
                '@keyframes talktokam-nod': {
                  '0%, 100%': { transform: 'rotate(0deg) scale(1)' },
                  '25%': { transform: 'rotate(-4deg) scale(1.03)' },
                  '75%': { transform: 'rotate(4deg) scale(1.03)' },
                },
              }}>
                {(botSettings.companyLogo || botSettings.avatar) ? (
                  <img src={botSettings.companyLogo || botSettings.avatar} alt="logo"
                    onError={(e) => { e.target.style.display = 'none'; const initial = (botSettings.botName || 'C')[0].toUpperCase().replace(/[<>"'&]/g, ''); const color = (headerColor || '#005f73').replace(/[^#\w\s,()%]/g, ''); e.target.parentElement.innerHTML = '<span style=\"width:64px;height:64px;border-radius:50%;background:' + color + ';display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800;color:#fff\">' + initial + '</span>'; }}
                    style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Avatar sx={{ width: 64, height: 64, bgcolor: headerColor, fontSize: 26, fontWeight: 800 }}>
                    {(botSettings.botName || 'C')[0].toUpperCase()}
                  </Avatar>
                )}
              </Box>
              {liveRequested && <FiberManualRecordIcon sx={{ position: 'absolute', bottom: 2, right: 2, fontSize: 12, color: '#fbbf24', bgcolor: '#fff', borderRadius: '50%', p: '1px', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />}
            </Box>
            <Typography fontWeight={800} fontSize={17} sx={{ mt: 1.5 }}>
              {botSettings.botName || 'Chat with us'}
            </Typography>
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.3, px: 1 }}>
              👋 {(() => { const h = new Date().getHours(); return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : h < 21 ? 'Good Evening' : 'Hey there'; })()}
            </Typography>
          </Box>

          {/* Pending live agent hint */}
          {pendingLiveAgent && (
            <Box sx={{ mb: 1.2, p: 1.2, bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgentIcon sx={{ fontSize: 18, color: '#e65100' }} />
              <Typography fontSize={11.5} fontWeight={700} color="#e65100">Enter your WhatsApp number and we'll connect you to a live agent</Typography>
            </Box>
          )}

          {/* Pre-chat phone form (single primary path) */}
          <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, p: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
            <TextField
              inputRef={phoneInputRef}
              fullWidth
              size="small"
              type="tel"
              placeholder="Enter your WhatsApp number..."
              value={phoneInput}
              onChange={e => { setPhoneInput(e.target.value.replace(/[^0-9]/g, '')); setPhoneError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleStartChat()}
              error={!!phoneError}
              helperText={phoneError}
              sx={{ mb: 1.2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
              InputProps={{ startAdornment: <Box sx={{ color: '#9ca3af', mr: 1, fontSize: 14 }}>📱</Box> }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleStartChat()}
              disabled={startingChat || !phoneInput.trim()}
              endIcon={startingChat ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                borderRadius: 2, py: 1.1, fontWeight: 700, fontSize: 13,
                bgcolor: headerColor, '&:hover': { bgcolor: headerColor, filter: 'brightness(0.9)' },
                textTransform: 'none',
              }}
            >
              {startingChat ? 'Starting...' : t(botSettings.defaultLanguage, 'startChat')}
            </Button>
          </Box>
          <Typography fontSize={10} color="#9ca3af" sx={{ textAlign: 'center', mt: 1.5 }}>
            🔒 Your info is private and secure
          </Typography>
        </Box>
      ) : (
        <>
          {/* Messages */}
          <Box
            ref={chatBodyRef}
            sx={{
              flex: 1, overflowY: 'auto', p: 2, position: 'relative',
              ...(shake && {
                animation: 'talktokamShake 0.4s ease',
                '@keyframes talktokamShake': { '0%,100%': { transform: 'translateX(0)' }, '20%,60%': { transform: 'translateX(-6px)' }, '40%,80%': { transform: 'translateX(6px)' } }
              }),
            }}
          >
            {messages.map((msg, i) => {
              const msgQuestion = msg.sender === 'bot' && msg.questionId ? flow.find(f => f.id === msg.questionId) : null;
              const msgMedia = msgQuestion?.media;
              const prevMsg = messages[i - 1];
              const nextMsg = messages[i + 1];
              const isFirst = !prevMsg || prevMsg.sender !== msg.sender;
              const isLast = !nextMsg || nextMsg.sender !== msg.sender;
              const showTime = isLast;
              const msgTime = msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: isLast ? 1.5 : 0.3,
                    px: 0.5,
                    animation: `msgSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1) ${Math.min(i * 0.04, 0.3)}s both`,
                    '@keyframes msgSlideIn': {
                      from: { opacity: 0, transform: msg.sender === 'user' ? 'translateX(16px) scale(0.95)' : 'translateX(-16px) scale(0.95)' },
                      to: { opacity: 1, transform: 'translateX(0) scale(1)' },
                    },
                  }}
                >
                  {(msg.sender === 'admin' || msg.sender === 'agent') && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.8, maxWidth: '78%' }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: isFirst ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: isFirst ? '0 2px 8px rgba(102,126,234,0.4)' : 'none',
                        visibility: isLast ? 'visible' : 'hidden',
                      }}>
                        <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>
                          {(msg.agentName || agentName || 'S').charAt(0).toUpperCase()}
                        </Typography>
                      </Box>
                      <Box>
                        {isFirst && <Typography sx={{ fontSize: 10, color: '#667eea', fontWeight: 700, mb: 0.3, px: 0.5 }}>
                          {msg.agentName || agentName || 'Support'}
                        </Typography>}
                        <Box sx={{
                          px: 2, py: 1.2,
                          borderRadius: isFirst ? '16px 16px 16px 4px' : '4px 16px 16px 4px',
                          background: 'linear-gradient(135deg, #f8f9ff, #f0f2ff)',
                          border: '1px solid rgba(102,126,234,0.15)',
                          boxShadow: '0 2px 12px rgba(102,126,234,0.1)',
                        }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#2d3748', fontSize }}>{msg.text}</Typography>
                        </Box>
                        {showTime && <Typography sx={{ fontSize: 9, color: '#a0aec0', mt: 0.3, px: 0.5 }}>{msgTime}</Typography>}
                      </Box>
                    </Box>
                  )}
                  {msg.sender !== 'admin' && msg.sender !== 'agent' && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.8, maxWidth: '78%', flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row' }}>
                      {msg.sender === 'bot' && !msg.isError && avatarUrl && isLast && (
                        <img src={avatarUrl} alt="bot" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
                      )}
                      {msg.sender === 'bot' && !msg.isError && avatarUrl && !isLast && (
                        <Box sx={{ width: 24, flexShrink: 0 }} />
                      )}
                      <Box>
                        {msg.text === "__FLOW_COMPLETE__" ? (
                          <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', border: '1px solid #bbf7d0', textAlign: 'center', minWidth: 200 }}>
                            <Typography sx={{ fontSize: 28, mb: 0.5 }}>🎉</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#166534', mb: 0.5 }}>Thank You!</Typography>
                            <Typography sx={{ fontSize: 12, color: '#4b5563' }}>We've received your response. Our team will get back to you soon.</Typography>
                          </Box>
                        ) : (
                          <>
                            {msgMedia && (
                              <Box sx={{ borderRadius: '12px 12px 0 0', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', borderBottom: 'none' }}>
                                <img src={msgMedia} alt="media" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
                              </Box>
                            )}
                            <Box sx={{
                              px: 2, py: 1.2,
                              borderTopLeftRadius: msgMedia ? 0 : undefined,
                              borderTopRightRadius: msgMedia ? 0 : undefined,
                              borderRadius: msgMedia ? '0 0 12px 12px' : getBubbleRadius(msg.sender),
                              background: msg.isError
                                ? '#fef2f2'
                                : msg.isSkip
                                  ? 'transparent'
                                  : msg.sender === 'user'
                                    ? `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`
                                    : `linear-gradient(135deg, ${questionColor}f0, ${questionColor})`,
                              color: msg.isError ? '#dc2626' : (msg.isSkip ? headerColor : (msg.sender === 'user' ? '#fff' : (isLightQuestion ? '#2d3748' : '#fff'))),
                              fontStyle: msg.isSkip ? 'italic' : 'normal',
                              fontWeight: msg.isSkip ? 600 : 400,
                              boxShadow: msg.isSkip ? 'none' : (msg.sender === 'user' ? `0 3px 12px ${optionColor}40` : '0 2px 8px rgba(0,0,0,0.08)'),
                              border: msg.isError ? '1px solid #fecaca' : (msg.isSkip ? `1.5px dashed ${headerColor}` : 'none'),
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}>
                              {msg.isSkip && <span style={{ fontSize: 14 }}>⏭</span>}
                              {msg._isUploading ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                                  <Box sx={{ display: 'inline-flex', gap: 0.3 }}>
                                    {[0, 1, 2].map((d) => (
                                      <Box key={d} sx={{
                                        width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.4,
                                        animation: `talktokamBounce 1s infinite ${d * 0.15}s`,
                                        '@keyframes talktokamBounce': { '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.3 }, '40%': { transform: 'scale(1)', opacity: 1 } },
                                      }} />
                                    ))}
                                  </Box>
                                  <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize, fontStyle: 'italic' }}>Bot is reading your file...</Typography>
                                </Box>
                              ) : msg.sender === 'user' && msg.text?.startsWith('[IMG]') ? (
                                <img src={msg.text.replace('[IMG]', '')} alt="upload" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }} />
                              ) : msg.sender === 'user' && /^📎\s.*\s(?:https?:\/\/|data:)\S+$/.test(msg.text || '') ? (
                                (() => {
                                  const t = msg.text || '';
                                  const url = t.slice(t.lastIndexOf(' ') + 1);
                                  const name = t.replace(/^📎\s*/, '').replace(url, '').trim();
                                  return <a href={url} target="_blank" rel="noopener noreferrer" download={name} style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all', fontSize }}>📎 {name}</a>;
                                })()
                              ) : msg.sender === 'bot' ? (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize, whiteSpace: 'pre-wrap', '& p': { m: 0 }, '& ul, & ol': { m: 0, pl: 2.5 } }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.text || '') }} />
                              ) : (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</Typography>
                              )}
                            </Box>
                            {showTime && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start', mt: 0.3, px: 0.5 }}>
                                <Typography sx={{ fontSize: 9, color: '#a0aec0' }}>{msgTime}</Typography>
                                {msg.sender === 'user' && (() => {
                                  // Find last user message index
                                  const lastUserIdx = messages.map((m, idx) => m.sender === 'user' ? idx : -1).filter(x => x >= 0).pop();
                                  const isLastUser = i === lastUserIdx;
                                  const isSeen = messageSeen && isLastUser;
                                  return (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <DoneIcon sx={{ fontSize: 11, color: isSeen ? '#60a5fa' : '#a0aec0', ml: '-6px', transition: 'color 0.4s ease' }} />
                                      <DoneIcon sx={{ fontSize: 11, color: isSeen ? '#60a5fa' : '#a0aec0', transition: 'color 0.4s ease' }} />
                                    </Box>
                                  );
                                })()}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {agentTyping && liveRequested && (
              <Box sx={{
                display: 'flex', alignItems: 'flex-end', gap: 0.8, mb: 1, px: 0.5,
                animation: 'msgSlideIn 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                '@keyframes msgSlideIn': { from: { opacity: 0, transform: 'translateX(-12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
              }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(102,126,234,0.4)',
                }}>
                  <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>
                    {(agentName || 'S').charAt(0).toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{
                  px: 2, py: 1.2, borderRadius: '16px 16px 16px 4px',
                  background: 'linear-gradient(135deg, #f8f9ff, #f0f2ff)',
                  border: '1px solid rgba(102,126,234,0.15)',
                  boxShadow: '0 2px 12px rgba(102,126,234,0.1)',
                  display: 'flex', gap: 0.6, alignItems: 'center',
                }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 7, height: 7, borderRadius: '50%', bgcolor: '#667eea',
                      animation: 'typingBounce 1.4s infinite ease-in-out',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes typingBounce': { '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.4 }, '40%': { transform: 'scale(1)', opacity: 1 } },
                    }} />
                  ))}
                </Box>
              </Box>
            )}

            {aiTyping && (
              <Box sx={{
                display: 'flex', alignItems: 'flex-end', gap: 0.8, mb: 1, px: 0.5,
                animation: 'msgSlideIn 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                '@keyframes msgSlideIn': { from: { opacity: 0, transform: 'translateX(-12px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
              }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="bot" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
                ) : (
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${questionColor}, ${questionColor}dd)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>B</Typography>
                  </Box>
                )}
                <Box sx={{ px: 2, py: 1.2, borderRadius: '16px 16px 16px 4px',
                  background: `linear-gradient(135deg, ${questionColor}f0, ${questionColor})`,
                  border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex', gap: 0.6, alignItems: 'center',
                }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 7, height: 7, borderRadius: '50%', bgcolor: isLightQuestion ? '#94a3b8' : '#cbd5e0',
                      animation: 'typingBounce 1.4s infinite ease-in-out',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes typingBounce': { '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.4 }, '40%': { transform: 'scale(1)', opacity: 1 } },
                    }} />
                  ))}
                </Box>
              </Box>
            )}

            {hasOptions && (
              <Box sx={{ mb: 1.5, pl: avatarUrl ? '32px' : '4px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ maxWidth: '85%' }}>
                  {isDropdown ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Choose an option</InputLabel>
                        <Select
                          value={dropdownVal}
                          label="Choose an option"
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '__other__') {
                              setDropdownVal('__other__');
                              setOtherText('');
                            } else {
                              handleOptionSelect(val);
                            }
                          }}
                          sx={{ borderRadius: 2, bgcolor: '#fff' }}
                        >
                          {visibleOptions.map((opt, i) => (
                            <MenuItem key={i} value={getOptionLabel(opt)}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {currentQ.imageChoices?.[i]?.image && (
                                  <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} />
                                )}
                                {getOptionLabel(opt)}
                              </Box>
                            </MenuItem>
                          ))}
                          {hasOtherOption && <MenuItem value="__other__">Other</MenuItem>}
                        </Select>
                      </FormControl>
                      {dropdownVal === '__other__' && (
                        <TextField
                          fullWidth size="small" placeholder="Type your answer..."
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && otherText.trim()) {
                              handleOptionSelect(otherText.trim());
                            }
                          }}
                          autoFocus
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                        />
                      )}
                    </Box>
                  ) : isList ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                      {visibleOptions.map((opt, i) => (
                        <Button
                          key={i}
                          fullWidth
                          onClick={() => handleOptionSelect(getOptionLabel(opt))}
                          sx={{
                            borderRadius: `${Math.max(borderRadius, 8)}px`,
                            background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
                            color: '#fff', border: 'none', textTransform: 'none', fontWeight: 600,
                            fontSize: Math.max(parseFloat(fontSize) - 1, 12) + 'px',
                            px: 2.5, py: 1.1,
                            justifyContent: 'flex-start',
                            boxShadow: `0 3px 10px ${optionColor}30`,
                            display: 'flex', alignItems: 'center', gap: 1,
                            '&:hover': { filter: 'brightness(1.1)', transform: 'translateY(-1px)', boxShadow: `0 6px 16px ${optionColor}50` },
                            '&:active': { transform: 'translateY(0)' },
                          }}
                        >
                          {currentQ.imageChoices?.[i]?.image && (
                            <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
                          )}
                          {getOptionLabel(opt)}
                        </Button>
                      ))}
                      {hasOtherOption && (
                        <Button
                          fullWidth
                          onClick={() => { setOtherText(''); setDropdownVal('__other__'); }}
                          sx={{
                            borderRadius: `${Math.max(borderRadius, 8)}px`,
                            color: optionColor,
                            border: `1.5px dashed ${optionColor}80`,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: Math.max(parseFloat(fontSize) - 1, 12) + 'px',
                            px: 2.5, py: 1.1, bgcolor: 'transparent',
                            justifyContent: 'flex-start',
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: `${optionColor}08`, transform: 'translateY(-1px)' },
                          }}
                        >
                          + Other
                        </Button>
                      )}
                      {hasOtherOption && dropdownVal === '__other__' && (
                        <TextField
                          fullWidth size="small" placeholder="Type your answer and press Enter..."
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && otherText.trim()) {
                              handleOptionSelect(otherText.trim());
                            }
                          }}
                          autoFocus
                          sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexDirection: layoutDir }}>
                        {visibleOptions.map((opt, i) => (
                          <Button
                            key={i}
                            onClick={() => handleOptionSelect(getOptionLabel(opt))}
                            sx={{
                              borderRadius: `${Math.max(borderRadius, 8)}px`,
                              background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
                              color: '#fff', border: 'none', textAlign: 'center', textTransform: 'none', fontWeight: 600,
                              fontSize: Math.max(parseFloat(fontSize) - 1, 12) + 'px',
                              px: 2.5, py: 1, minWidth: 80,
                              flex: layoutDir === 'column' ? '1 1 100%' : '0 1 auto',
                              boxShadow: `0 3px 10px ${optionColor}30`,
                              display: 'flex', alignItems: 'center', gap: 0.8,
                              '&:hover': { filter: 'brightness(1.1)', transform: 'translateY(-1px)', boxShadow: `0 6px 16px ${optionColor}50` },
                              '&:active': { transform: 'translateY(0)' },
                            }}
                          >
                            {currentQ.imageChoices?.[i]?.image && (
                              <img src={currentQ.imageChoices[i].image} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover' }} />
                            )}
                            {getOptionLabel(opt)}
                          </Button>
                        ))}
                        {hasOtherOption && (
                          <Button
                            onClick={() => { setOtherText(''); setDropdownVal('__other__'); }}
                            sx={{
                              borderRadius: `${Math.max(borderRadius, 8)}px`,
                              color: optionColor,
                              border: `1.5px dashed ${optionColor}80`,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: Math.max(parseFloat(fontSize) - 1, 12) + 'px',
                              px: 2.5, py: 1, bgcolor: 'transparent',
                              flex: layoutDir === 'column' ? '1 1 100%' : '0 1 auto',
                              transition: 'all 0.2s ease',
                              '&:hover': { bgcolor: `${optionColor}08`, transform: 'translateY(-1px)' },
                            }}
                          >
                            + Other
                          </Button>
                        )}
                      </Box>
                      {hasOtherOption && dropdownVal === '__other__' && (
                        <TextField
                          fullWidth size="small" placeholder="Type your answer and press Enter..."
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && otherText.trim()) {
                              handleOptionSelect(otherText.trim());
                            }
                          }}
                          autoFocus
                          sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                        />
                      )}
                    </Box>
                  )}
                  <Typography sx={{ mt: 1, color: '#aaa', fontSize: 11 }}>
                    {isDropdown ? 'Pick an option above' : (hasOtherOption ? 'Tap an option or type your own' : t(botSettings.defaultLanguage, 'tapOption'))}
                  </Typography>
                </Box>
                {currentQ?.skipOption && !currentQ?.required && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                      sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                      Skip this question
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Special type inputs — shown in chat area below question */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'rating' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ p: '46px', bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', width: 250 }}>
                  {(() => {
                    const rType = currentQ?.settings?.ratingType || 'stars';
                    const labels = currentQ?.settings?.ratingLabels || ['Terrible', 'Bad', 'Okay', 'Good', 'Great'];
                    const smilies = ['😡', '😟', '😐', '🙂', '😍'];
                    const activeIdx = ratingHover !== null ? ratingHover : 2;
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ transition: 'all 0.3s ease', transform: ratingHover !== null ? 'scale(1.1)' : 'scale(1)' }}>
                          <Typography sx={{ fontSize: '90px', lineHeight: 1 }}>
                            {rType === 'smilies' ? smilies[activeIdx] : rType === 'numbers' ? `${activeIdx + 1}` : '⭐'}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{labels[activeIdx]}</Typography>
                        <Box sx={{ display: 'flex', gap: 0 }}>
                          {[0, 1, 2, 3, 4].map(i => (
                            <Box key={i}
                              onMouseEnter={() => setRatingHover(i)}
                              onMouseLeave={() => setRatingHover(null)}
                              onClick={() => { withAdvanceGuard(() => { setRatingHover(null); pushMessage('user', `${labels[i]} (${i + 1}/5)`, currentQ?.id); advanceStep(step + 1); }); }}
                              sx={{
                                cursor: 'pointer', p: 0.5, transition: 'all 0.2s',
                                transform: ratingHover === i ? 'scale(1.3)' : 'scale(1)'
                              }}>
                              <Typography sx={{ fontSize: '26px' }}>
                                {rType === 'smilies' ? smilies[i] : rType === 'numbers' ? `${i + 1}` : '⭐'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                  {currentQ?.skipOption && !currentQ?.required && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                        Skip this question
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {!done && !liveRequested && questionSpoken && currentQ?.type === 'date_picker' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField type="date" size="small" fullWidth value={text} onChange={e => setText(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <Button variant="contained" size="small" disabled={!text}
                    onClick={() => { withAdvanceGuard(() => { pushMessage('user', text, currentQ?.id); setText(''); advanceStep(step + 1); }); }}
                    sx={{ borderRadius: 2, minWidth: 44, bgcolor: headerColor }}><SendIcon fontSize="small" /></Button>
                </Box>
              </Box>
            )}

            {!done && !liveRequested && questionSpoken && currentQ?.type === 'time_picker' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField type="time" size="small" fullWidth value={text} onChange={e => setText(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <Button variant="contained" size="small" disabled={!text}
                    onClick={() => { withAdvanceGuard(() => { pushMessage('user', text, currentQ?.id); setText(''); advanceStep(step + 1); }); }}
                    sx={{ borderRadius: 2, minWidth: 44, bgcolor: headerColor }}><SendIcon fontSize="small" /></Button>
                </Box>
              </Box>
            )}

            {!done && !liveRequested && questionSpoken && currentQ?.type === 'range' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{currentQ?.settings?.range?.min || 0}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: headerColor }}>{text || currentQ?.settings?.range?.min || 0}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{currentQ?.settings?.range?.max || 100}</Typography>
                </Box>
                <input type="range" min={currentQ?.settings?.range?.min || 0} max={currentQ?.settings?.range?.max || 100}
                  value={text || currentQ?.settings?.range?.min || 0} onChange={e => setText(e.target.value)}
                  style={{ width: '100%', accentColor: headerColor }} />
                <Button variant="contained" size="small" fullWidth
                  onClick={() => { withAdvanceGuard(() => { pushMessage('user', text || String(currentQ?.settings?.range?.min || 0), currentQ?.id); setText(''); advanceStep(step + 1); }); }}
                  sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontSize: 12, bgcolor: headerColor }}>Submit</Button>
              </Box>
            )}

            {!done && !liveRequested && questionSpoken && currentQ?.type === 'multiple_options' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexDirection: currentQ?.flexDirection || 'row' }}>
                  {(currentQ?.options || []).map((opt, i) => {
                    const label = typeof opt === 'object' ? (opt.label || opt.value) : opt;
                    const selected = multiSelected.includes(label);
                    return (
                      <Button key={i}
                        onClick={() => setMultiSelected(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label])}
                        startIcon={<span style={{ fontSize: 14 }}>{selected ? '☑' : '☐'}</span>}
                        sx={{
                          borderRadius: `${Math.max(borderRadius, 8)}px`,
                          background: selected ? `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)` : 'transparent',
                          color: selected ? '#fff' : optionColor,
                          border: selected ? 'none' : `1.5px solid ${optionColor}80`,
                          textTransform: 'none', fontWeight: 600,
                          fontSize: Math.max(parseFloat(fontSize) - 1, 12) + 'px',
                          px: 2.5, py: 1, minWidth: 80,
                          boxShadow: selected ? `0 3px 10px ${optionColor}30` : 'none',
                          '&:hover': { filter: 'brightness(1.05)', transform: 'translateY(-1px)', bgcolor: selected ? undefined : `${optionColor}08` }
                        }}>
                        {label}
                      </Button>
                    );
                  })}
                </Box>
                <Button
                  fullWidth variant="contained" disabled={multiSelected.length === 0}
                  onClick={() => {
                    if (multiSelected.length === 0) return;
                    const answer = multiSelected.join(', ');
                    withAdvanceGuard(() => { pushMessage('user', answer, currentQ?.id); advanceStep(getNextStep(step, answer)); });
                  }}
                  sx={{ mt: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' }, '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
                >
                  {multiSelected.length > 0 ? `Submit (${multiSelected.length} selected)` : 'Select options'}
                </Button>
                {currentQ?.skipOption && !currentQ?.required && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                      sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                      Skip this question
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* File Upload */}
            {/* Dynamic Question — fetch options from API */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'dynamic_question' && (
              <DynamicQuestionInput
                currentQ={currentQ}
                optionColor={optionColor}
                headerColor={headerColor}
                onSelect={(val) => { withAdvanceGuard(() => { pushMessage('user', val, currentQ?.id); advanceStep(getNextStep(step, val)); }); }}
                onSkip={handleSkip}
                pendingSkip={pendingSkip}
              />
            )}

            {/* Real Time Search */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'Real_time_search' && (
              <RealTimeSearchInput
                currentQ={currentQ}
                headerColor={headerColor}
                onSelect={(val) => { withAdvanceGuard(() => { pushMessage('user', val, currentQ?.id); advanceStep(getNextStep(step, val)); }); }}
                onSkip={handleSkip}
                pendingSkip={pendingSkip}
              />
            )}

            {/* Carousel */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'carousel_with_buttons' && (
              <Box sx={{ px: 1, pb: 1.5, overflow: 'hidden' }}>
                <Box sx={{
                  display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, pt: 0.5,
                  scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#d0d0d0', borderRadius: 2 },
                }}>
                  {(currentQ?.options || []).map((card, i) => {
                    const c = typeof card === 'object' ? card : { title: String(card), description: '', image: '', button: { text: 'Select', action: 'select' } };
                    return (
                      <Box key={i} sx={{
                        minWidth: 200, maxWidth: 220, flex: '0 0 auto',
                        scrollSnapAlign: 'start',
                        borderRadius: 3, overflow: 'hidden',
                        bgcolor: '#fff', boxShadow: '0 3px 15px rgba(0,0,0,0.1)',
                        border: '1px solid #f0f0f0',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 25px rgba(0,0,0,0.15)' },
                      }}>
                        {c.image && (
                          <Box sx={{ width: '100%', height: 120, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                            <img src={c.image} alt={c.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </Box>
                        )}
                        <Box sx={{ p: 1.5 }}>
                          {c.title && (
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2937', mb: 0.3, lineHeight: 1.3 }}>
                              {c.title}
                            </Typography>
                          )}
                          {c.description && (
                            <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 1, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {c.description}
                            </Typography>
                          )}
                          <Button
                            fullWidth
                            size="small"
                            onClick={() => handleOptionSelect(c.title || c.button?.text || `Option ${i + 1}`)}
                            sx={{
                              borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12,
                              bgcolor: headerColor, color: '#fff',
                              '&:hover': { bgcolor: headerColor, filter: 'brightness(0.9)' },
                            }}
                          >
                            {c.button?.text || 'Select'}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {currentQ?.skipOption && !currentQ?.required && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                      sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                      Skip this question
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Order Items */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'order_items' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                  {(currentQ?.options || []).map((item, i) => {
                    const o = typeof item === 'object' ? item : { name: String(item), price: 0, qty: 1, image: '' };
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderBottom: i < (currentQ?.options?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        {o.image && (
                          <Box sx={{ width: 48, height: 48, borderRadius: 2, overflow: 'hidden', flexShrink: 0, bgcolor: '#f9fafb' }}>
                            <img src={o.image} alt={o.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{o.name}</Typography>
                          {o.price && <Typography sx={{ fontSize: 12, fontWeight: 700, color: headerColor }}>₹{o.price}</Typography>}
                        </Box>
                        <Button
                          size="small"
                          onClick={() => handleOptionSelect(o.name)}
                          sx={{
                            borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 11,
                            bgcolor: headerColor, color: '#fff', px: 2, whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: headerColor, filter: 'brightness(0.9)' },
                          }}
                        >
                          {o.buttonText || 'Add'}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
                {currentQ?.skipOption && !currentQ?.required && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                      sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                      Skip this question
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {!done && !liveRequested && questionSpoken && currentQ?.type === 'file_upload' && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box onClick={() => document.getElementById('talktokam-file-input')?.click()}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 1,
                      px: 2, py: 1.2, cursor: 'pointer',
                      borderRadius: 2, border: `1.5px dashed ${headerColor}`,
                      bgcolor: 'rgba(0,0,0,0.02)', color: headerColor,
                      fontSize: 12, fontWeight: 700, textTransform: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: `${headerColor}10`, transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${headerColor}30` },
                    }}>
                    <span style={{ fontSize: 16 }}>📎</span>
                    Choose a file
                  </Box>
                  <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>JPG, PNG, PDF, DOC · max {currentQ?.settings?.uploadLimit ?? 10}MB</Typography>
                </Box>
                <input id="talktokam-file-input" type="file" hidden accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.webp"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const maxMb = currentQ?.settings?.uploadLimit ?? 10;
                    if (file.size > maxMb * 1024 * 1024) { setMessages(prev => [...prev, { sender: 'bot', text: `❌ File too large. Max ${maxMb}MB.`, isError: true }]); return; }
                    const isImage = file.type.startsWith('image/');
                    setMessages(prev => [...prev, { sender: 'user', text: `📎 ${file.name}`, _fileName: file.name, _isUploading: true }]);
                    const clearPlaceholder = () => setMessages(prev => prev.filter(m => !(m._fileName === file.name && m._isUploading)));

                    try {
                      const url = await uploadViaBackend(file);
                      const msgText = isImage ? `[IMG]${url}` : `📎 ${file.name} ${url}`;
                      clearPlaceholder();
                      pushMessage('user', msgText, currentQ?.id);
                      advanceStep(getNextStep(step, file.name));
                    } catch (err) {
                      clearPlaceholder();
                      setMessages(prev => [...prev, { sender: 'bot', text: '❌ Upload failed. Please try again.', isError: true }]);
                    }
                  }} />
                {currentQ?.skipOption && !currentQ?.required && (
                  <Box sx={{ mt: 1.2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                      sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                      Skip this question
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Form — multi-field form */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'form' && (() => {
              const formFields = currentQ?.settings?.fields || [];
              const submitText = currentQ?.settings?.submitText || 'Submit';
              return (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  {formFields.length === 0 ? (
                    <Typography sx={{ fontSize: 11, color: '#9ca3af', p: 2, textAlign: 'center', border: '1.5px dashed #e5e7eb', borderRadius: 2 }}>
                      No fields configured. Add fields in the admin to enable this form.
                    </Typography>
                  ) : (
                    <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, p: 1.5, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        {formFields.map((f, i) => {
                          const fId = `talktokam-form-${currentQ?.id}-${i}`;
                          const fieldKey = f.name || `field_${i}`;
                          const baseInputSx = {
                            '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 12, bgcolor: '#fafbff' },
                            '& .MuiInputLabel-root': { fontSize: 11 },
                          };
                          return (
                            <Box key={i}>
                              {f.type === 'textarea' ? (
                                <TextField
                                  fullWidth multiline minRows={2} size="small"
                                  id={fId}
                                  label={f.label || ''}
                                  placeholder={f.placeholder || ''}
                                  required={!!f.required}
                                  onChange={(e) => setFormValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                                  sx={baseInputSx}
                                />
                              ) : f.type === 'select' ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`${fId}-label`}>{f.label || ''}</InputLabel>
                                  <Select
                                    labelId={`${fId}-label`}
                                    id={fId}
                                    label={f.label || ''}
                                    value={formValues?.[fieldKey] || ''}
                                    onChange={(e) => setFormValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                                    sx={{ borderRadius: 1.5, fontSize: 12, bgcolor: '#fafbff' }}
                                  >
                                    {(f.options || []).filter(Boolean).map((opt, oi) => (
                                      <MenuItem key={oi} value={opt} sx={{ fontSize: 12 }}>{opt}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : f.type === 'radio' ? (
                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#374151', mb: 0.6 }}>{f.label || ''}{f.required ? ' *' : ''}</Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                    {(f.options || []).filter(Boolean).map((opt, oi) => (
                                      <Box key={oi}
                                        onClick={() => setFormValues(prev => ({ ...prev, [fieldKey]: opt }))}
                                        sx={{
                                          display: 'inline-flex', alignItems: 'center', gap: 0.6, cursor: 'pointer',
                                          px: 1.2, py: 0.6, borderRadius: 2, fontSize: 11, fontWeight: 600,
                                          border: '1.5px solid', transition: 'all 0.15s',
                                          borderColor: formValues?.[fieldKey] === opt ? headerColor : '#e5e7eb',
                                          bgcolor: formValues?.[fieldKey] === opt ? `${headerColor}10` : '#fafbff',
                                          color: formValues?.[fieldKey] === opt ? headerColor : '#374151',
                                        }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid', borderColor: formValues?.[fieldKey] === opt ? headerColor : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          {formValues?.[fieldKey] === opt && <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: headerColor }} />}
                                        </Box>
                                        {opt}
                                      </Box>
                                    ))}
                                  </Box>
                                </FormControl>
                              ) : f.type === 'checkbox' ? (
                                <Box
                                  onClick={() => setFormValues(prev => ({ ...prev, [fieldKey]: !prev?.[fieldKey] }))}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer', p: 0.5, borderRadius: 1.5, '&:hover': { bgcolor: '#f9fafb' } }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: 0.6, border: '2px solid', borderColor: formValues?.[fieldKey] ? headerColor : '#d1d5db', bgcolor: formValues?.[fieldKey] ? headerColor : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                                    {formValues?.[fieldKey] && <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 800, lineHeight: 1 }}>✓</Typography>}
                                  </Box>
                                  <Typography sx={{ fontSize: 12, color: '#374151' }}>{f.label || ''}{f.required ? ' *' : ''}</Typography>
                                </Box>
                              ) : (
                                <TextField
                                  fullWidth size="small"
                                  id={fId}
                                  type={f.type === 'phone' ? 'tel' : f.type === 'email' ? 'email' : f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                                  label={f.label || ''}
                                  placeholder={f.placeholder || ''}
                                  required={!!f.required}
                                  InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                                  onChange={(e) => setFormValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                                  sx={baseInputSx}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                      <Button
                        fullWidth variant="contained" onClick={() => {
                          const missing = formFields.filter((f, i) => {
                            if (!f.required) return false;
                            const v = formValues[f.name || `field_${i}`];
                            if (f.type === 'checkbox') return !v;
                            return v === undefined || v === null || String(v).trim() === '';
                          });
                          if (missing.length) {
                            setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || `Please fill: ${missing.map(m => m.label || m.name).join(', ')}`, isError: true }]);
                            return;
                          }
                          // Format validation for filled email / phone / number fields
                          const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          const badField = formFields.find((f, i) => {
                            const v = String(formValues[f.name || `field_${i}`] ?? '').trim();
                            if (!v) return false;
                            if (f.type === 'email') return !emailRe.test(v);
                            if (f.type === 'phone') { const d = v.replace(/\D/g, ''); return d.length < 7 || d.length > 15; }
                            if (f.type === 'number') return isNaN(Number(v));
                            return false;
                          });
                          if (badField) {
                            setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || `Please enter a valid ${badField.label || badField.name}.`, isError: true }]);
                            return;
                          }
                          const payload = {};
                          formFields.forEach((f, i) => { payload[f.name || `field_${i}`] = formValues[f.name || `field_${i}`] ?? ''; });
                          const summary = formFields.map((f, i) => `${f.label || f.name || `Field ${i + 1}`}: ${payload[f.name || `field_${i}`]}`).join('\n');
                          pushMessage('user', summary, currentQ?.id);
                          advanceStep(getNextStep(step, summary));
                          setFormValues({});
                        }}
                        sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' } }}
                      >
                        {submitText}
                      </Button>
                    </Box>
                  )}
                  {currentQ?.skipOption && !currentQ?.required && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                        Skip this question
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Ask Contacts — pre-defined contact fields */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'ask_contacts' && (() => {
              const fieldSettings = currentQ?.settings?.fields || { name: { enabled: true, required: true }, email: { enabled: true, required: true }, phone: { enabled: false, required: false } };
              const submitText = currentQ?.settings?.submitText || 'Submit';
              const fields = [
                { key: 'name', label: fieldSettings.name?.label || 'Full Name', placeholder: fieldSettings.name?.placeholder || 'Enter your name', required: !!fieldSettings.name?.required, type: 'text', enabled: fieldSettings.name?.enabled !== false, icon: '👤' },
                { key: 'email', label: fieldSettings.email?.label || 'Email', placeholder: fieldSettings.email?.placeholder || 'you@example.com', required: !!fieldSettings.email?.required, type: 'email', enabled: fieldSettings.email?.enabled !== false, icon: '✉' },
                { key: 'phone', label: fieldSettings.phone?.label || 'Phone', placeholder: fieldSettings.phone?.placeholder || '+91 98765 43210', required: !!fieldSettings.phone?.required, type: 'tel', enabled: !!fieldSettings.phone?.enabled, icon: '☎' },
              ].filter((f) => f.enabled);
              return (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, p: 1.5, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      {fields.map((f) => (
                        <TextField
                          key={f.key}
                          fullWidth size="small"
                          id={`talktokam-ask-${currentQ?.id}-${f.key}`}
                          type={f.type}
                          label={`${f.icon} ${f.label}${f.required ? ' *' : ''}`}
                          placeholder={f.placeholder}
                          required={f.required}
                          onChange={(e) => setFormValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 12, bgcolor: '#fafbff' }, '& .MuiInputLabel-root': { fontSize: 11 } }}
                        />
                      ))}
                    </Box>
                    <Button
                      fullWidth variant="contained" onClick={() => {
                        const missing = fields.filter((f) => {
                          if (!f.required) return false;
                          const v = formValues[f.key];
                          return v === undefined || v === null || String(v).trim() === '';
                        });
                        if (missing.length) {
                          setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || `Please fill: ${missing.map(m => m.label).join(', ')}`, isError: true }]);
                          return;
                        }
                        // Format validation for filled email / phone fields
                        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        const badField = fields.find((f) => {
                          const v = String(formValues[f.key] ?? '').trim();
                          if (!v) return false;
                          if (f.type === 'email') return !emailRe.test(v);
                          if (f.type === 'tel') { const d = v.replace(/\D/g, ''); return d.length < 7 || d.length > 15; }
                          return false;
                        });
                        if (badField) {
                          setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || `Please enter a valid ${badField.label}.`, isError: true }]);
                          return;
                        }
                        const lines = fields.map((f) => `${f.label}: ${formValues[f.key] || ''}`);
                        const summary = lines.join('\n');
                        pushMessage('user', summary, currentQ?.id);
                        advanceStep(getNextStep(step, summary));
                        setFormValues({});
                      }}
                      sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' } }}
                    >
                      {submitText}
                    </Button>
                  </Box>
                  {currentQ?.skipOption && !currentQ?.required && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                        Skip this question
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Appointment Booking — date + time slot */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'appointment_booking' && (() => {
              const s = currentQ?.settings || {};
              const startHour = s.startHour || '09:00';
              const endHour = s.endHour || '17:00';
              const duration = Number(s.duration) || 30;
              const workingDays = s.workingDays || [1, 2, 3, 4, 5];
              const minDate = s.minDate || '';
              const maxDate = s.maxDate || '';
              const confirmMsg = s.confirmationMessage || 'Your appointment is confirmed!';

              const generateLocalSlots = () => {
                const out = [];
                let [sh, sm] = startHour.split(':').map(Number);
                let [eh, em] = endHour.split(':').map(Number);
                let cur = sh * 60 + sm;
                const end = eh * 60 + em;
                while (cur + duration <= end) {
                  const h = Math.floor(cur / 60);
                  const m = cur % 60;
                  out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                  cur += duration;
                }
                return out;
              };
              const slots = calendlySlots.length > 0 ? calendlySlots : generateLocalSlots();

              const isWorkingDay = (dateStr) => {
                if (!dateStr) return false;
                return workingDays.includes(new Date(dateStr).getDay());
              };

              return (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, p: 1.5, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>📅 Pick a date</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        value={appointmentDate ? dayjs(appointmentDate) : null}
                        onChange={(newDate) => {
                          setAppointmentDate(newDate.format('YYYY-MM-DD'));
                          setAppointmentSlot('');
                        }}
                        shouldDisableDate={(date) => {
                          if (!workingDays.includes(date.day())) return true;
                          if (minDate && date.isBefore(dayjs(minDate), 'day')) return true;
                          if (maxDate && date.isAfter(dayjs(maxDate), 'day')) return true;
                          return false;
                        }}
                        slotProps={{
                          day: {
                            sx: {
                              '&.Mui-selected': { bgcolor: headerColor, '&:hover': { bgcolor: headerColor } },
                              '&.MuiPickersDay-today': { borderColor: headerColor },
                            }
                          }
                        }}
                        sx={{ width: '100%', maxWidth: 320, mx: 'auto', mb: 1, '& .MuiPickersCalendarHeader-label': { fontWeight: 700, fontSize: 13 } }}
                      />
                    </LocalizationProvider>

                    {appointmentDate && isWorkingDay(appointmentDate) && (
                      <>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>⏰ Pick a time</Typography>
                        {calendlyLoading ? (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={20} sx={{ color: headerColor }} />
                          </Box>
                        ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: 0.6, mb: 1.5 }}>
                            {slots.length > 0 ? slots.map((slot) => (
                              <Box key={slot} onClick={() => setAppointmentSlot(slot)}
                                sx={{
                                  py: 0.8, textAlign: 'center', borderRadius: 1.5, fontSize: 12, fontWeight: 700,
                                  cursor: 'pointer', transition: 'all 0.15s', border: '1.5px solid',
                                  borderColor: appointmentSlot === slot ? headerColor : '#e5e7eb',
                                  bgcolor: appointmentSlot === slot ? `${headerColor}10` : '#fafbff',
                                  color: appointmentSlot === slot ? headerColor : '#374151',
                                  '&:hover': { borderColor: headerColor, transform: 'translateY(-1px)' },
                                }}>
                                {slot}
                              </Box>
                            )) : (
                              <Typography sx={{ fontSize: 11, color: '#9ca3af', gridColumn: '1 / -1', textAlign: 'center', py: 1 }}>
                                No available slots for this date.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
                      <TextField
                        fullWidth size="small" required
                        label="Your Name"
                        value={appointmentName}
                        onChange={e => setAppointmentName(e.target.value)}
                        placeholder="John Doe"
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />,
                          sx: { borderRadius: 1.5, fontSize: 12, bgcolor: '#fafbff' }
                        }}
                      />
                      <TextField
                        fullWidth size="small" type="email" required
                        label="Your Email"
                        value={appointmentEmail}
                        onChange={e => setAppointmentEmail(e.target.value)}
                        placeholder="you@example.com"
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />,
                          sx: { borderRadius: 1.5, fontSize: 12, bgcolor: '#fafbff' }
                        }}
                      />
                    </Box>

                    <Button
                      fullWidth variant="contained" disabled={!appointmentDate || !isWorkingDay(appointmentDate) || !appointmentSlot || !appointmentName.trim() || !appointmentEmail || !appointmentEmail.includes('@')}
                      onClick={() => {
                        const summary = `📅 ${appointmentDate} at ${appointmentSlot} | Name: ${appointmentName.trim()} | Email: ${appointmentEmail}`;
                        pushMessage('user', summary, currentQ?.id);
                        setMessages(prev => [...prev, { sender: 'bot', text: `⏳ Scheduling your meeting...\n\nName: ${appointmentName.trim()}\nDate: ${appointmentDate}\nTime: ${appointmentSlot}\n\nYou'll receive the confirmation link shortly.` }]);
                        advanceStep(getNextStep(step, summary));
                        setAppointmentDate('');
                        setAppointmentSlot('');
                        setAppointmentName('');
                        setAppointmentEmail('');
                      }}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' }, '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
                    >
                      Confirm Booking
                    </Button>
                  </Box>
                  {currentQ?.skipOption && !currentQ?.required && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                        Skip this question
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Authenticator — OTP verification */}
            {!done && !liveRequested && questionSpoken && currentQ?.type === 'authenticator' && (() => {
              const s = currentQ?.settings || {};
              const length = Number(s.length) || 6;
              const codeType = s.codeType || 'numeric';
              const method = s.method || 'email';
              const resendCooldown = Number(s.resendCooldown) || 60;
              const charRegex = codeType === 'numeric' ? /^[0-9]$/ : /^[a-zA-Z0-9]$/;

              const generateOtp = () => {
                if (codeType === 'numeric') {
                  let s = '';
                  for (let i = 0; i < length; i++) s += Math.floor(Math.random() * 10);
                  return s;
                }
                const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
                let s = '';
                for (let i = 0; i < length; i++) s += chars[Math.floor(Math.random() * chars.length)];
                return s;
              };

              const sendOtp = () => {
                const code = generateOtp();
                setOtpExpected(code);
                setOtpSent(true);
                setOtpDigits(Array(length).fill(''));
                setOtpResendIn(resendCooldown);
                const target = method === 'email' ? 'your email' : 'your phone';
                setMessages(prev => [...prev, { sender: 'bot', text: `🔐 A ${length}-digit code was sent to ${target}.\n\n📋 Demo code: ${code}\n(Real implementation sends via ${method === 'email' ? 'email' : 'SMS'} provider)` }]);
              };

              const verifyOtp = (digitsArg) => {
                // Accept digits from caller to avoid stale-closure bug (state may not be flushed yet via setTimeout)
                const digits = digitsArg || otpDigits;
                const entered = digits.join('');
                if (entered.length !== length) {
                  setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || `Please enter all ${length} digits.`, isError: true }]);
                  return;
                }

                const expected = String(otpExpected || '').toUpperCase();
                const actual = String(entered || '').toUpperCase();

                if (actual === expected) {
                  setMessages(prev => [...prev, { sender: 'bot', text: '✅ Verified successfully!' }]);
                  pushMessage('user', `[Verified OTP] ${entered}`, currentQ?.id);
                  advanceStep(getNextStep(step, entered));
                  // Important: clear after a short tick to avoid race with auto-verify
                  setTimeout(() => {
                    setOtpExpected('');
                    setOtpSent(false);
                    setOtpDigits([]);
                  }, 0);
                } else {
                  // Prevent double-firing verify from showing stale "incorrect" message.
                  setMessages(prev => [...prev, { sender: 'bot', text: currentQ?.errorMessage || 'Invalid code. Please try again.', isError: true }]);
                  setOtpDigits(Array(length).fill(''));
                }
              };

              return (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, p: 1.5, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                    {!otpSent ? (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Box sx={{ fontSize: 32, mb: 0.5 }}>🔐</Box>
                        <Typography sx={{ fontSize: 12, color: '#374151', mb: 1.5, fontWeight: 600 }}>Click below to receive a {length}-digit code via {method === 'email' ? 'email' : 'SMS'}.</Typography>
                        <Button variant="contained" onClick={sendOtp}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', px: 3, py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' } }}>
                          Send Code
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>Enter the {length}-digit code</Typography>
                        <Box sx={{ display: 'flex', gap: 0.6, mb: 1.5, justifyContent: 'center' }}>
                          {otpDigits.map((d, i) => (
                            <TextField
                              key={i}
                              id={`talktokam-otp-${currentQ?.id}-${i}`}
                              value={d}
                              onChange={(e) => {
                                const v = e.target.value.toUpperCase();
                                if (v && !charRegex.test(v)) return;
                                const u = [...otpDigits];
                                u[i] = v;
                                setOtpDigits(u);
                                if (v && i < length - 1) {
                                  const next = document.getElementById(`talktokam-otp-${currentQ?.id}-${i + 1}`);
                                  if (next) next.focus();
                                }
                                if (u.every((c) => c !== '')) setTimeout(() => verifyOtp(u), 200);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
                                  const prev = document.getElementById(`talktokam-otp-${currentQ?.id}-${i - 1}`);
                                  if (prev) prev.focus();
                                }
                              }}
                              onPaste={(e) => {
                                const text = (e.clipboardData.getData('text') || '').slice(0, length).toUpperCase();
                                if ([...text].every((c) => charRegex.test(c))) {
                                  e.preventDefault();
                                  setOtpDigits([...text.padEnd(length, '').slice(0, length)].map((c) => c || ''));
                                  if (text.length === length) setTimeout(() => verifyOtp([...text.padEnd(length, '').slice(0, length)].map((c) => c || '')), 200);
                                }
                              }}
                              sx={{ width: 36, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 16, fontWeight: 700, bgcolor: '#fafbff', height: 42, '& input': { textAlign: 'center', p: 0 } } }}
                            />
                          ))}
                        </Box>
                        <Button fullWidth variant="contained" onClick={() => verifyOtp()} disabled={otpDigits.some((d) => !d)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: headerColor, color: '#fff', py: 1, boxShadow: `0 4px 12px ${headerColor}40`, '&:hover': { bgcolor: headerColor, filter: 'brightness(1.05)' }, '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}>
                          Verify
                        </Button>
                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                          {otpResendIn > 0 ? (
                            <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>Resend code in {otpResendIn}s</Typography>
                          ) : (
                            <Box component="span" onClick={sendOtp} sx={{ fontSize: 11, color: headerColor, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Resend code</Box>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                  {currentQ?.skipOption && !currentQ?.required && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                        Skip this question
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Skip - in chat area for text questions */}
            {!done && !liveRequested && !hasOptions && !['rating', 'date_picker', 'time_picker', 'range', 'multiple_options', 'file_upload', 'carousel_with_buttons', 'order_items', 'dynamic_question', 'Real_time_search', 'form', 'ask_contacts', 'appointment_booking', 'authenticator'].includes(currentQ?.type) && questionSpoken && currentQ?.skipOption && !currentQ?.required && (
              <Box sx={{ px: 2, pb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={handleSkip}
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
                  Skip this question
                </Button>
              </Box>
            )}


          </Box>
          {/* â”€â”€â”€ CHAT CLOSED STATE â”€â”€â”€ */}
          {chatClosed ? (
            <Box sx={{ px: 2, pb: 2, pt: 1.5, bgcolor: '#fff' }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #f0f2ff, #faf5ff)', borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.15)', textAlign: 'center' }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5, boxShadow: '0 4px 16px rgba(102,126,234,0.3)' }}>
                  <Typography sx={{ fontSize: 22 }}>✅</Typography>
                </Box>
                <Typography fontSize={14} fontWeight={800} color="#2d3748" mb={0.5}>
                  Chat Resolved
                </Typography>
                <Typography fontSize={12} color="#718096" mb={2} lineHeight={1.5}>
                  Your conversation has been resolved.<br />Thank you for contacting us!
                </Typography>
                {/* Rating */}
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography fontSize={11} color="#6b7280" mb={0.8} textAlign="center">Rate your experience:</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map(s => (
                      <Box key={s} onClick={() => {
                        axios.post(`${CONV_API}/rate`, { chatbotId: chatId, sessionId: sessionIdRef.current, rating: s }).then(() => {
                          document.getElementById('talktokam-rating-box').innerHTML = '<p style="color:#059669;font-weight:700;font-size:13px;margin:8px 0">Thanks for your feedback! ⭐</p>';
                        }).catch(() => { });
                      }}
                        sx={{ cursor: 'pointer', fontSize: 26, transition: 'all 0.2s', '&:hover': { transform: 'scale(1.4)' } }}>
                        ⭐
                      </Box>
                    ))}
                  </Box>
                  <div id="talktokam-rating-box"></div>
                </Box>
                <Button fullWidth variant="contained" size="small"
                  onClick={handleConfirmClose}
                  sx={{
                    borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 13, mb: 1,
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    boxShadow: '0 3px 12px rgba(102,126,234,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #5a6fd6, #6a3f9a)' },
                  }}>
                  Start New Chat
                </Button>
                <Button fullWidth variant="outlined" size="small"
                  startIcon={reopening ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                  onClick={handleReopen} disabled={reopening}
                  sx={{
                    borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12,
                    borderColor: 'rgba(102,126,234,0.4)', color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102,126,234,0.06)' },
                  }}>
                  {reopening ? 'Reopening...' : 'Reopen Chat'}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Live Agent Card â€” shows after flow is done */}
              {done && (
                <Box sx={{ px: 2, pb: 1.5, bgcolor: '#fff' }}>
                  <Divider sx={{ mb: 1.5 }} />
                  {liveRequested ? (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                      bgcolor: agentName ? '#f0f2ff' : '#e8f5e9', borderRadius: 2,
                      border: agentName ? '1px solid rgba(102,126,234,0.2)' : 'none',
                    }}>
                      {agentName ? (
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{agentName.charAt(0).toUpperCase()}</Typography>
                        </Box>
                      ) : (
                        <CheckCircleOutlineIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
                      )}
                      <Box>
                        <Typography fontSize={13} fontWeight={700} color={agentName ? '#667eea' : '#2e7d32'}>
                          {agentName ? `${agentName} is here!` : 'Agent Notified!'}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">
                          {agentName ? 'You are now connected.' : "We'll connect you shortly."}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              )}

              {/* Live agent suggestion — shows when user mentions live agent keywords */}
              {showLiveSuggestion && !liveRequested && (
                <Box sx={{ px: 2, pb: 1, bgcolor: '#fff' }}>
                  <Button fullWidth variant="contained" size="small"
                    startIcon={liveLoading ? <CircularProgress size={14} color="inherit" /> : <SupportAgentIcon fontSize="small" />}
                    onClick={() => { setShowLiveSuggestion(false); handleRequestLive(); }}
                    disabled={liveLoading}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 13,
                      bgcolor: headerColor, '&:hover': { bgcolor: headerColor, filter: 'brightness(0.9)' },
                      boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                    }}>
                    {liveLoading ? 'Requesting...' : '🤝 Connect with Live Agent'}
                  </Button>
                </Box>
              )}

              {/* Footer - chat input with new icon bar + Talktokam branding */}
              <Box sx={{ bgcolor: '#fff' }}>

                {/* Input row */}
                <Box sx={{ p: 1.2, borderTop: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'flex-end' }}>
                    {/* Emoji picker */}
                    <Tooltip title="Emoji" placement="top">
                      <IconButton size="small" onClick={(e) => setEmojiAnchor(e.currentTarget)}
                        sx={{ color: '#6b7280', flexShrink: 0, '&:hover': { color: headerColor, bgcolor: `${headerColor}10` } }}>
                        <EmojiEmotionsIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    {/* Attach file */}
                    <Tooltip title="Attach file" placement="top">
                      <IconButton size="small" onClick={() => document.getElementById('talktokam-chat-attach-input')?.click()}
                        sx={{ color: '#6b7280', flexShrink: 0, '&:hover': { color: headerColor, bgcolor: `${headerColor}10` } }}>
                        <AttachFileIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                    <input id="talktokam-chat-attach-input" type="file" hidden
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleAttachFile(file);
                        e.target.value = '';
                      }}
                    />
                    <TextField
                      fullWidth size="small" multiline maxRows={4}
                      value={text}
                      placeholder={liveRequested ? 'Reply to agent...' : 'Type your answer...'}
                      onChange={e => {
                        setText(e.target.value);
                        if (validationError) setValidationError('');
                        if (liveRequested && !chatClosed) {
                          const s = connectSocket();
                          if (s) s.emit('visitor-typing', { chatbotId: chatId, sessionId: sessionIdRef.current });
                        }
                      }}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      disabled={(done && !liveRequested && !botSettings.aiEnabled) || hasOptions || (!liveRequested && questionSpoken && ['rating', 'date_picker', 'time_picker', 'range', 'multiple_options', 'file_upload', 'carousel_with_buttons', 'order_items', 'form', 'ask_contacts', 'appointment_booking', 'authenticator'].includes(currentQ?.type))}
                      error={!!validationError}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3, fontSize: 13, bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#fff' },
                          ...(validationError && { '& fieldset': { borderColor: '#dc2626', borderWidth: '1.5px' } }),
                        },
                        ...(shake && {
                          animation: 'talktokamShake 0.4s ease',
                          '@keyframes talktokamShake': {
                            '0%, 100%': { transform: 'translateX(0)' },
                            '20%, 60%': { transform: 'translateX(-6px)' },
                            '40%, 80%': { transform: 'translateX(6px)' },
                          },
                        }),
                      }}
                    />
                    {/* Mic — voice input */}
                    <Tooltip title={listening ? 'Listening… tap to stop' : 'Voice input'} placement="top">
                      <IconButton size="small" onClick={handleMicToggle}
                        sx={{
                          color: listening ? '#fff' : '#6b7280', flexShrink: 0,
                          bgcolor: listening ? '#ef4444' : 'transparent',
                          animation: listening ? 'talktokamMicPulse 1.4s infinite' : 'none',
                          '@keyframes talktokamMicPulse': {
                            '0%, 100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.5)' },
                            '50%': { boxShadow: '0 0 0 6px rgba(239,68,68,0)' },
                          },
                          '&:hover': {
                            color: listening ? '#fff' : headerColor,
                            bgcolor: listening ? '#dc2626' : `${headerColor}10`,
                          },
                        }}>
                        <MicIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      onClick={handleSend}
                      disabled={(done && !liveRequested && !botSettings.aiEnabled) || !text.trim() || hasOptions}
                      sx={{
                        borderRadius: 3, minWidth: 40, px: 1.2, py: 0.8, flexShrink: 0,
                        bgcolor: liveRequested ? '#e65100' : headerColor,
                        '&:hover': { bgcolor: liveRequested ? '#bf360c' : headerColor, filter: 'brightness(0.9)' },
                        '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </Button>
                  </Box>
                  {validationError && (
                    <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', gap: 0.5, color: '#dc2626', fontSize: 11, fontWeight: 600 }}>
                      <span>❌</span>
                      <span>{validationError}</span>
                    </Box>
            )}

          </Box>
                {/* Footer branding: privacy (left) + Talktokam (right) */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid #f0f0f0',
                }}>
                  <Typography fontSize={9.5} sx={{ color: '#9ca3af', fontWeight: 600 }}>
                    🔒 Your info is private and secure
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography fontSize={9.5} sx={{ color: '#9ca3af', fontWeight: 600, letterSpacing: 0.3 }}>
                      Powered by
                    </Typography>
                    <Typography fontSize={10} sx={{ color: headerColor, fontWeight: 800, letterSpacing: 0.2 }}>
                      Talktokam
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </>
      )}

      {/* ─── NEW UI: Hamburger menu, FAQ drawer, Emoji picker, Rating ─── */}
      <HamburgerMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        headerColor={headerColor}
        botSettings={botSettings}
        userName={userName}
        liveRequested={liveRequested}
        onHome={() => { setMenuOpen(false); setPreChatDone(false); setMessages([]); setStep(0); setDone(false); setPendingLiveAgent(false); }}
        onRestart={handleRestartConversation}
        onFAQ={() => { setMenuOpen(false); setFaqOpen(true); }}
        onTransfer={handleTransferClick}
        onCloseChat={() => setConfirmClose(true)}
      />
      <FAQPanel open={faqOpen} onClose={() => setFaqOpen(false)} headerColor={headerColor} botSettings={botSettings} />
      <EmojiPicker
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        onPick={(e) => setText(prev => prev + e)}
      />

    </Box>
  );
};

// ─── Dynamic Question Component ───
const DynamicQuestionInput = ({ currentQ, optionColor, headerColor, onSelect, onSkip, pendingSkip }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const apiUrl = currentQ?.settings?.apiEndpoint;
    if (!apiUrl) { setLoading(false); setError('No API endpoint configured'); return; }

    fetch(apiUrl, {
      method: currentQ?.settings?.apiMethod || 'GET',
      headers: { 'Content-Type': 'application/json' },
      ...(currentQ?.settings?.apiMethod === 'POST' ? { body: JSON.stringify(currentQ?.settings?.apiBody || {}) } : {}),
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.options || data.data || data.results || []);
        setOptions(list.map(o => typeof o === 'object' ? (o.label || o.name || o.value || String(o)) : String(o)));
        setLoading(false);
      })
      .catch(() => { setError('Failed to load options'); setLoading(false); });
  }, [currentQ]);

  if (loading) return <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <Box sx={{ px: 2, pb: 1.5 }}><Typography sx={{ fontSize: 12, color: '#dc2626' }}>{error}</Typography></Box>;

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map((opt, i) => (
          <Button key={i} onClick={() => onSelect(opt)}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12,
              px: 2, py: 1, border: 'none', color: '#fff',
              background: `linear-gradient(135deg, ${optionColor}, ${optionColor}dd)`,
              boxShadow: `0 3px 10px ${optionColor}30`,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${optionColor}50`,
                background: `linear-gradient(135deg, ${optionColor}, ${optionColor})`,
                filter: 'brightness(1.05)',
              },
              '&:active': { transform: 'translateY(0)' },
            }}>
            {opt}
          </Button>
        ))}
      </Box>
      {currentQ?.skipOption && !currentQ?.required && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={onSkip}
            sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor || optionColor, border: `1.5px dashed ${headerColor || optionColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor || optionColor}10`, borderColor: headerColor || optionColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor || optionColor}30` } }}>
            Skip this question
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Real Time Search Component ───
const RealTimeSearchInput = ({ currentQ, headerColor, onSelect, onSkip, pendingSkip }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleSearch = useCallback((val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const minChars = currentQ?.settings?.minChars ?? 2;
    if (!val.trim() || val.trim().length < minChars) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const apiUrl = currentQ?.settings?.apiEndpoint || '';
        const paramName = currentQ?.settings?.queryParam || 'q';
        const sep = apiUrl.includes('?') ? '&' : '?';
        const url = `${apiUrl}${sep}${paramName}=${encodeURIComponent(val)}`;
        const res = await fetch(url);
        const data = await res.json();
        let list = Array.isArray(data) ? data : null;
        const rPath = (currentQ?.settings?.responsePath || '').trim();
        if (!list) {
          if (rPath && rPath !== 'auto') {
            list = data;
            rPath.split('.').forEach(p => { if (list && typeof list === 'object') list = list[p]; });
          } else {
            list = data?.options || data?.data || data?.results || data?.items || [];
          }
        }
        if (!Array.isArray(list)) list = [];
        const labelField = currentQ?.settings?.labelField || 'label';
        const valueField = currentQ?.settings?.valueField || 'value';
        setResults(list.map(o => typeof o === 'object' && o !== null
          ? (o[labelField] || o[valueField] || o.name || o.title || String(o))
          : String(o)));
      } catch { setResults([]); }
      setLoading(false);
    }, currentQ?.settings?.debounceMs ?? 300);
  }, [currentQ]);

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <TextField
        fullWidth size="small" value={query}
        placeholder="Type to search..."
        onChange={e => handleSearch(e.target.value)}
        sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={16} /> : null,
        }}
      />
      {results.length > 0 && (
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
          {results.map((opt, i) => (
            <Box key={i} onClick={() => onSelect(opt)}
              sx={{
                px: 2, py: 1.2, cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid #f3f4f6' : 'none',
                transition: 'background 0.15s', fontSize: 13, color: '#374151',
                '&:hover': { bgcolor: `${headerColor}10` },
              }}>
              {opt}
            </Box>
          ))}
        </Box>
      )}
      {query && !loading && results.length === 0 && (
        <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1 }}>No results found</Typography>
      )}
      {currentQ?.skipOption && !currentQ?.required && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button startIcon={<span style={{ fontSize: 14 }}>⏭</span>} onClick={onSkip}
            sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: headerColor, border: `1.5px dashed ${headerColor}`, borderRadius: 2, px: 1.5, py: 0.6, bgcolor: 'rgba(0,0,0,0.02)', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${headerColor}10`, borderColor: headerColor, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${headerColor}30` } }}>
            Skip this question
          </Button>
        </Box>
      )}
      {query && !loading && results.length === 0 && (
        <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1 }}>No results found</Typography>
      )}
    </Box>
  );
};

export default UserMessage;
